import { 
  PenTool, Lightbulb, FileText, Eye, Calendar, Settings, Rocket, Package, Globe, 
  FileEdit, Upload, ClipboardList, Palette, Sparkles, ImageIcon, History, Send, 
  Mail, BarChart3, Users, BookOpen, Zap, Heart, ShoppingCart, Search, Target, 
  Wand2, Brain, Microscope, TrendingDown, CheckSquare, Tag, Tags, LinkIcon, Link2, AlertTriangle, 
  FlaskConical, TrendingUp, MessageSquare, MessageCircle, Play, Clock, Database, Cog, UserSquare, 
  UserCheck, FolderOpen, Phone, BarChart, CalendarDays, Rss, UsersRound, 
  LayoutDashboard, Tv, Library, BarChart2, Gamepad2, Mic, Store, MapPin, 
  Smartphone, Youtube, Activity, Kanban, Plug, Fingerprint, FolderSearch, Crosshair, Rabbit,
  FlagTriangleRight, BadgeDollarSign, Cpu, BrainCircuit, ScanFace, Waypoints, UserRoundSearch, Focus, Radical, Megaphone, Code, Percent, GraduationCap, ShieldCheck, Network, Contact, Webhook, Wrench, AlarmClockCheck, Star, Newspaper, ListChecks, ClipboardCheck, Briefcase, Share2, UserPlus, Plus,
  Building2, Home, FileCheck, CalendarCheck, TrendingUp as TrendingUpIcon, MapPin as MapPinIcon, DollarSign, Briefcase as BriefcaseIcon, RefreshCw, Banknote, Columns3, Album, FolderKanban, Calendar as CalendarIcon, Vote, Award, TowerControl, Bot
} from 'lucide-react';
import twoY3XIcon from "@/assets/2y3x-icon.png";
import about2y3xIcon from "@/assets/about-2y3x-icon.webp";
import growthHackingIcon from "@/assets/growth-hacking-icon.png";
import digcompIcon from "@/assets/digcomp/pentagon-menu-icon.png";

export interface MenuNode {
  id: string;
  label: string;
  path?: string;
  children?: MenuNode[];
  icon?: any;
  description?: string;        // Short 1-2 sentence summary for UI/tooltips
  businessContext?: string;    // Detailed context for AI training and documentation
  featureKey?: string;         // Feature key from brandFeatures.ts - item will only show if feature is enabled
}

// Menu structure with all application modules
export const COMPLETE_MENU_STRUCTURE: MenuNode[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: Lightbulb,
    description: 'Central hub showing key metrics, insights, and quick access to main features',
    businessContext: 'Marketing managers and team leads use this overview to monitor campaign performance, track KPIs, and get a birds-eye view of all marketing activities across channels. It serves as the starting point for daily workflows and provides real-time visibility into brand health.'
  },
  {
    id: 'newsfeedStatic',
    label: "Today's News",
    path: '/newsfeed',
    icon: Newspaper,
    description: 'Aggregated news feed from configured sources',
    businessContext: 'Users can view curated news and content feeds from RSS sources and other configured content streams.'
  },
  {
    id: 'start-here',
    label: 'Start Here',
    path: '/start-here',
    icon: Rocket,
    description: 'Onboarding guide for new users to understand platform features and workflows',
    businessContext: 'New team members and stakeholders use this guided tour to learn the platform\'s capabilities and best practices. It reduces onboarding time and ensures consistent understanding of available tools and processes.'
  },
  {
    id: 'digcomp',
    label: 'DigComp 3.0',
    icon: digcompIcon,
    description: 'DigComp 3.0 European Digital Competence Framework - Track and develop digital skills',
    businessContext: 'Training administrators manage the DigComp 3.0 framework including 5 competence areas, 21 competences, and 8 proficiency levels. Track student digital skill development, manage self-assessments, and issue competence badges and certificates.',
    children: [
      {
        id: 'digcomp-statistics',
        label: 'Overall Statistics',
        path: '/digcomp/statistics',
        icon: Target,
        description: 'DigComp analytics and performance metrics',
        businessContext: 'View aggregated statistics on digital competence development across all students and courses.'
      },
      {
        id: 'digcomp-overview',
        label: 'Overview',
        path: '/digcomp/overview',
        icon: Target,
        description: 'DigComp 3.0 framework overview',
        businessContext: 'Get a high-level view of the DigComp framework and its implementation status.'
      },
      {
        id: 'digcomp-framework',
        label: 'The Framework',
        path: '/digcomp/framework',
        icon: Target,
        description: 'Competences, proficiency levels, and learning outcomes',
        businessContext: 'Manage the 5 competence areas, 21 competences, 8 proficiency levels, and learning outcome statements.'
      },
      {
        id: 'digcomp-mapping-overview',
        label: 'Mapping Overview',
        path: '/digcomp/mapping-overview',
        icon: Target,
        description: 'View and moderate all DigComp mappings across content',
        businessContext: 'Holistic view of events, podcasts, courses, quizzes, and questions linked to DigComp competences.'
      },
      {
        id: 'digcomp-student-progress',
        label: 'Student Progress',
        path: '/digcomp/student-progress',
        icon: Target,
        description: 'Track individual student competence development',
        businessContext: 'Monitor and analyze individual student progress across all digital competences.'
      },
      {
        id: 'digcomp-assessments',
        label: 'Assessments',
        path: '/digcomp/assessments',
        icon: Target,
        description: 'Manage DigComp assessments and evaluations',
        businessContext: 'Create and manage self-assessments and evaluations for measuring digital competence.'
      },
      {
        id: 'digcomp-badges-certificates',
        label: 'Badges & Certificates',
        path: '/digcomp/badges-certificates',
        icon: Target,
        description: 'Manage competence badges and certificates',
        businessContext: 'Issue and manage digital badges and certificates for competence achievements.'
      },
    ]
  },
  {
    id: 'annotations-group',
    label: 'Annotations',
    icon: ImageIcon,
    description: 'Capture and manage UI snapshots with comments and reports',
    businessContext: 'Teams use annotations to document UI feedback, create visual reports, and collaborate on design reviews.',
    children: [
      { 
        id: 'annotations', 
        label: 'Annotations', 
        path: '/annotations', 
        icon: ImageIcon,
        description: 'View and manage captured UI snapshots',
        businessContext: 'Users review, organize, and manage annotated screenshots of the application interface.'
      },
      { 
        id: 'annotation-reports', 
        label: 'Reports', 
        path: '/annotations/reports', 
        icon: FileText,
        description: 'Create and share annotation reports as presentations',
        businessContext: 'Teams compile annotations into shareable Gamma presentations for stakeholder reviews and documentation.'
      },
    ]
  },
  {
    id: 'brand-management',
    label: 'Brand Management',
    icon: Fingerprint,
    description: 'Centralized brand identity, assets, and team management',
    businessContext: 'Brand managers configure and maintain brand foundations including identity, visual assets, team members, and product catalog. This serves as the single source of truth for all brand-related information used throughout marketing campaigns.',
    children: [
      { 
        id: 'brand-setup', 
        label: 'Brand Basic Data', 
        path: '/brand-setup', 
        icon: Zap,
        description: 'Configure core brand information including name, industry, target audience, and value propositions',
        businessContext: 'Brand strategists define fundamental brand attributes that inform all marketing activities. This data trains AI assistants and ensures consistent brand representation across all content and campaigns.'
      },
      { 
        id: 'brand-onboarding-questionnaire', 
        label: 'Brand Onboarding Questionnaire', 
        path: '/brand-onboarding-questionnaire', 
        icon: FileText,
        description: 'Guided questionnaire to capture comprehensive brand strategy and positioning',
        businessContext: 'Marketing directors complete this structured assessment to document brand positioning, competitive advantages, audience segments, and strategic goals. Responses feed into AI content generation and strategic recommendations.'
      },
      { 
        id: 'brand-team', 
        label: 'Brand Team', 
        path: '/brand-team', 
        icon: Users,
        description: 'Manage team members, roles, and access permissions for brand workspaces',
        businessContext: 'Agency account managers and brand administrators assign team roles and permissions to control who can view, edit, and approve brand content. Supports multi-brand agency workflows and client collaboration.'
      },
      { 
        id: 'brand-marktech', 
        label: 'Brand Marktech', 
        path: '/brand-marktech', 
        icon: Zap,
        description: 'Connect and manage marketing technology integrations for data sync and automation',
        businessContext: 'Marketing operations teams integrate tools like Google Analytics, CRM systems, and social platforms to enable data-driven insights and automated workflows. Reduces manual data entry and improves reporting accuracy.'
      },
      { 
        id: 'brand-products', 
        label: 'Products, Services & Hooks', 
        path: '/brand-products', 
        icon: ShoppingCart,
        description: 'Catalog of products, services, and key selling points with detailed attributes',
        businessContext: 'Product marketers maintain the product/service catalog with features, benefits, and unique selling propositions. This inventory powers content generation, SEO optimization, and targeted campaign creation across all channels.'
      },
      { 
        id: 'brand-moodboard', 
        label: 'Brand Moodboard', 
        path: '/brand-moodboard', 
        icon: Heart,
        description: 'Visual inspiration library defining brand aesthetics and creative direction',
        businessContext: 'Creative directors curate reference images, color palettes, and visual styles that guide AI image generation and designer workflows. Ensures brand consistency in visual content across all marketing materials.'
      },
      { 
        id: 'brand-assets', 
        label: 'Brand Assets', 
        path: '/brand-assets', 
        icon: ImageIcon,
        description: 'Digital asset library for logos, images, documents, and brand materials',
        businessContext: 'Marketing coordinators organize and access approved brand assets for use in campaigns, content, and communications. Centralized storage ensures teams always use current, on-brand materials and prevents outdated asset usage.'
      },
      { 
        id: 'testimonials', 
        label: 'People Testimonials', 
        path: '/testimonials', 
        icon: Star,
        description: 'Manage customer testimonials, reviews, and social proof content',
        businessContext: 'Marketing teams collect, organize, and distribute customer testimonials across channels to build trust and credibility. Supports manual collection, public submission forms, webhook integrations, and embeddable widgets for website display.'
      },
      { 
        id: 'client-logos', 
        label: 'Client Logos', 
        path: '/client-logos', 
        icon: ImageIcon,
        description: 'Showcase client and partner logos in organized galleries',
        businessContext: 'Marketing teams organize and display client, partner, and customer logos to build credibility and demonstrate business relationships. Galleries can be published to courses, embedded on websites, or distributed via API for multi-channel logo display.'
      },
      { 
        id: 'product-enrichment', 
        label: 'Product Enrichment', 
        path: '/brand-products/enrichment', 
        icon: ShoppingCart,
        description: 'AI-powered product data enhancement with SEO-optimized descriptions and attributes',
        businessContext: 'E-commerce managers use AI to generate compelling product descriptions, extract key features, and optimize metadata for search engines. Scales product content creation while maintaining quality and consistency.',
        featureKey: 'product_enrichment',
      },
      { 
        id: 'product-mapping', 
        label: 'Product Mapping', 
        path: '/product-mapping', 
        icon: Waypoints,
        description: 'Map products to CRM projects for unified analytics and reporting',
        businessContext: 'Real estate and product managers link brand products to CRM projects to enable cross-platform analytics, unified reporting, and data synchronization between marketing and sales systems.',
        featureKey: 'product_mapping',
      },
      { 
        id: 'product-report', 
        label: 'Product Report', 
        path: '/product_report', 
        icon: BarChart3,
        description: 'Comprehensive product analytics dashboard with multi-source data',
        businessContext: 'Product managers analyze product performance across channels including Google Analytics, proposals, social posts, and conversion funnels to make data-driven decisions.',
        featureKey: 'product_report',
      },
      { 
        id: 'engagement-points', 
        label: 'Engagement Points', 
        path: '/engagement-points', 
        icon: Webhook,
        description: 'Define key customer touchpoints and form submissions across pages',
        businessContext: 'Marketing and sales teams map out customer engagement touchpoints like forms, CTAs, and conversion points. Links engagement to products and business stages for tracking and optimization.'
      },
      { 
        id: 'landing-pages', 
        label: 'Landing Pages', 
        path: '/landing-pages', 
        icon: Globe,
        description: 'Manage and analyze landing pages with AI-powered insights',
        businessContext: 'Marketing teams track and optimize landing page content, CTAs, and performance with AI analysis for better conversion optimization.'
      },
      { 
        id: 'business-stages', 
        label: 'Business Stages', 
        path: '/business-stages', 
        icon: Waypoints,
        description: 'Configure business lifecycle stages for engagement tracking',
        businessContext: 'Define and manage business lifecycle stages (Awareness, Pre-Launch, Early-Traction, Growth) to categorize engagement points and track customer journey progression.'
      },
    ]
  },
  {
    id: 'performance-marketing',
    label: 'Paid Ads',
    icon: Megaphone,
    description: 'Paid advertising campaign analysis and optimization across platforms',
    businessContext: 'Performance marketers analyze ad spend efficiency, creative performance, and ROI across Google, LinkedIn, and Facebook ads to optimize budget allocation and campaign effectiveness.',
    children: [
      { 
        id: 'performance-brief', 
        label: 'Briefs', 
        path: '/performance-brief', 
        icon: FileText,
        description: 'Generate marketing briefs with personas, countries, and engagement points',
        businessContext: 'Marketing teams compile and export performance marketing briefs for agencies and paid media teams, including personas, target markets, and conversion points.'
      },
      { 
        id: 'performance-marketing-dashboard', 
        label: 'Dashboard', 
        path: '/performance-marketing-dashboard', 
        icon: Lightbulb,
        description: 'Unified view of paid advertising performance across all platforms',
        businessContext: 'Marketing directors monitor cross-platform ad spend, conversions, and ROI to identify top-performing channels and reallocate budget for maximum return on advertising investment.'
      },
      { 
        id: 'utm-builder', 
        label: 'UTM Builder', 
        path: '/utm-builder', 
        icon: Link2,
        description: 'Generate standardized UTM parameters for campaign tracking',
        businessContext: 'Digital marketers create consistent UTM codes for all paid campaigns to ensure accurate attribution tracking in analytics, enabling proper channel and campaign performance measurement.'
      },
      {
        id: 'google-ads',
        label: 'Google Ads',
        description: 'Google Ads campaign performance analysis and insights',
        businessContext: 'PPC specialists analyze Google Search and Display campaigns to optimize keyword bids, ad copy, and landing page performance for improved quality scores and conversion rates.',
        children: [
          { 
            id: 'googleads-ads-analysis', 
            label: 'Google Ads Analysis', 
            path: '/googleads-ads-analysis', 
            icon: Target,
            description: 'Detailed performance analytics for Google Ads campaigns',
            businessContext: 'SEM managers examine ad performance by campaign, ad group, keyword, and device to identify winners, pause underperformers, and uncover optimization opportunities that improve ROAS.'
          },
          { 
            id: 'googleads-ads-ai-insights', 
            label: 'Google Ads AI Insights', 
            path: '/googleads-ads-ai-insights', 
            icon: Brain,
            description: 'AI-powered recommendations for Google Ads optimization',
            businessContext: 'Marketing analysts receive automated insights on bid adjustments, budget reallocation, negative keywords, and ad copy improvements based on historical performance patterns and predictive modeling.'
          },
          { 
            id: 'google-ads-processing', 
            label: 'Google Ads Processing', 
            path: '/google-ads-processing', 
            icon: Upload,
            description: 'Import and process Google Ads performance data',
            businessContext: 'Data analysts upload Google Ads reports for centralized analysis, combining paid search data with other marketing channels to understand full-funnel attribution and campaign interactions.'
          },
          { 
            id: 'google-ads-spend-by-product', 
            label: 'Spend by Product', 
            path: '/google-ads-spend-by-product', 
            icon: BarChart3,
            description: 'Ad spend allocation and performance by product or service line',
            businessContext: 'Product marketers track which products receive ad investment and their relative performance to align paid strategy with business priorities and profitability goals.'
          }
        ]
      },
      {
        id: 'linkedin-ads',
        label: 'LinkedIn Ads',
        description: 'LinkedIn advertising campaign performance and B2B targeting insights',
        businessContext: 'B2B marketers evaluate LinkedIn Ads effectiveness by audience segment, job title targeting, and content format to reach decision-makers and generate high-quality leads.',
        children: [
          { 
            id: 'linkedin-ads-analysis', 
            label: 'LinkedIn Ads Analysis', 
            path: '/linkedin-ads-analysis', 
            icon: Target,
            description: 'Performance analytics for LinkedIn advertising campaigns',
            businessContext: 'Account-based marketers analyze engagement rates, lead quality, and cost per lead by industry, company size, and job function to refine targeting for enterprise accounts.'
          },
          { 
            id: 'linkedin-ads-ai-insights', 
            label: 'LinkedIn Ads AI Insights', 
            path: '/linkedin-ads-ai-insights', 
            icon: Brain,
            description: 'AI-powered insights for LinkedIn Ads optimization',
            businessContext: 'Marketing strategists receive recommendations on audience expansion, ad creative variations, and bidding strategies based on performance trends and competitive intelligence.'
          },
          { 
            id: 'linkedin-ads-processing', 
            label: 'LinkedIn Ads Processing', 
            path: '/linkedin-ads-processing', 
            icon: Upload,
            description: 'Import and process LinkedIn Ads campaign data',
            businessContext: 'Campaign managers import LinkedIn performance reports to unify B2B advertising analytics with other marketing touchpoints for accurate lead attribution.'
          }
        ]
      },
      {
        id: 'meta-ads',
        label: 'Meta Ads',
        description: 'Facebook and Instagram advertising performance analysis',
        businessContext: 'Social advertisers optimize Meta campaigns across awareness, engagement, and conversion objectives to maximize reach and return on ad spend.',
        children: [
          { 
            id: 'meta-ads-analysis', 
            label: 'Meta Ads Analysis', 
            path: '/meta-ads-analysis', 
            icon: Target,
            description: 'Performance analytics for Facebook and Instagram ads',
            businessContext: 'Social media buyers analyze audience performance, creative fatigue, and placement effectiveness to maintain high engagement rates and prevent ad performance decay.'
          },
          { 
            id: 'meta-ads-ai-insights', 
            label: 'Meta Ads AI Insights', 
            path: '/meta-ads-ai-insights', 
            icon: Brain,
            description: 'AI-powered recommendations for Meta Ads optimization',
            businessContext: 'Growth marketers receive automated suggestions on lookalike audiences, creative refresh timing, and budget pacing based on historical campaign patterns.'
          },
          { 
            id: 'meta-ads-processing', 
            label: 'Meta Ads Processing', 
            path: '/meta-ads-processing', 
            icon: Upload,
            description: 'Import and process Meta Ads campaign data',
            businessContext: 'Data teams centralize Facebook and Instagram advertising data for cross-platform analysis and unified social media ROI reporting.'
          }
        ]
      },
      { 
        id: 'creative-performance', 
        label: 'Creative Analysis', 
        path: '/creative-performance', 
        icon: ImageIcon,
        description: 'Visual asset performance analysis across paid campaigns',
        businessContext: 'Creative strategists identify which images, videos, and ad formats drive the highest engagement and conversions to inform future creative production and testing.'
      }
    ]
  },
  {
    id: 'content-marketing',
    label: 'Content',
    icon: PenTool,
    description: 'Content creation and management across all formats and channels',
    businessContext: 'Content marketers orchestrate the entire content lifecycle from ideation to publication across long-form articles, social media, creative assets, newsroom content, podcasts, and interactive experiences. This hub centralizes content operations for consistent brand storytelling.',
    children: [
      {
        id: 'articles',
        label: 'Articles',
        icon: FileText,
        description: 'SEO-optimized article creation and management system',
        businessContext: 'Content creators produce, optimize, and manage articles with built-in SEO analysis, readability scoring, and structured data support for maximum search visibility and engagement.',
        children: [
          {
            id: 'articles-list',
            label: 'All Articles',
            path: '/articles',
            icon: FileText,
            description: 'Browse and manage all articles with SEO scores and status tracking',
            businessContext: 'Content managers view, filter, and organize the complete article library with real-time SEO and readability scores to prioritize optimization efforts.'
          },
          {
            id: 'article-categories',
            label: 'Categories',
            path: '/articles/categories',
            icon: FolderOpen,
            description: 'Organize articles into SEO-friendly category hierarchies',
            businessContext: 'Content strategists create and manage article categories to improve site architecture, user navigation, and search engine understanding of content relationships.'
          },
          {
            id: 'article-tags',
            label: 'Tags',
            path: '/articles/tags',
            icon: Tag,
            description: 'Manage article tags for cross-linking and topic clustering',
            businessContext: 'SEO specialists maintain article tags to enable topic clustering, improve internal linking, and help readers discover related content.'
          },
          {
            id: 'article-authors',
            label: 'Authors',
            path: '/articles/authors',
            icon: UserSquare,
            description: 'Manage article authors and their profiles',
            businessContext: 'Content managers create and maintain author profiles with expertise areas, social links, and bios to enable rich author attribution on articles for SEO and reader credibility.'
          },
          {
            id: 'media-library',
            label: 'Media Library',
            path: '/articles/media-library',
            icon: ImageIcon,
            description: 'Manage images and media files for articles',
            businessContext: 'Content creators upload, organize, and manage images from multiple sources including uploads, AI-generated images, and document hub for use in articles and content.'
          },
          {
            id: 'article-typography',
            label: 'Typography Settings',
            path: '/articles/typography',
            icon: FileText,
            description: 'Configure heading and body text styles for articles',
            businessContext: 'Content administrators configure typography settings including font sizes, weights, families, and spacing for headings and body text to ensure consistent article presentation.'
          }
        ]
      },
      {
        id: 'long-form-content',
        label: 'Long-Form Content',
        icon: Brain,
        description: 'Blog posts, articles, and in-depth content creation workflows',
        businessContext: 'Content strategists produce SEO-optimized articles and thought leadership content that drives organic traffic, establishes authority, and generates leads. Includes both human-driven ideation and SEO-informed research approaches.',
        children: [
          {
            id: 'human-driven-content',
            label: 'Human Driven Content',
            icon: Lightbulb,
            description: 'Creative content ideation and production based on brand expertise',
            businessContext: 'Editorial teams generate original content ideas from brand insights, industry trends, and creative brainstorming. This approach emphasizes thought leadership and unique perspectives over keyword optimization.',
            children: [
              { 
                id: 'upload-content-ideas', 
                label: '0. Upload Content Ideas', 
                path: '/upload-content-ideas', 
                icon: Upload,
                description: 'Bulk import content ideas from spreadsheets or external sources',
                businessContext: 'Marketing managers upload content calendars from client meetings or brainstorming sessions to centralize planning. Supports CSV/Excel uploads to streamline workflow integration and reduce manual entry.'
              },
              { 
                id: 'content-ideas', 
                label: '1. Generate Content Ideas', 
                path: '/content-ideas', 
                icon: Lightbulb,
                description: 'AI-powered content topic suggestions based on brand voice and audience interests',
                businessContext: 'Content leads use AI to overcome writer\'s block and discover trending topics aligned with brand positioning. The system analyzes brand data, industry trends, and audience preferences to suggest high-value content angles.'
              },
              { 
                id: 'enhanced-content-ideas', 
                label: '1.2 Enhanced Content Ideas', 
                path: '/enhanced-content-ideas', 
                icon: Sparkles,
                description: 'Advanced content suggestions with deeper research and competitive analysis',
                businessContext: 'Senior editors leverage enhanced AI analysis that includes competitive gap analysis, trending subtopics, and strategic content cluster recommendations to dominate specific topic areas.'
              },
              { 
                id: 'cm-posts-suggested', 
                label: '2. Review Generated Posts', 
                path: '/cm-posts-suggested', 
                icon: Eye,
                description: 'Review and refine AI-generated blog post drafts before publication',
                businessContext: 'Content editors evaluate AI-generated long-form content for accuracy, brand voice alignment, and quality. They can approve, request revisions, or enhance drafts before moving to publication queue.'
              },
              { 
                id: 'competitors-content', 
                label: 'Competitor Content Analysis', 
                path: '/competitors-content', 
                icon: Target,
                description: 'Track and analyze competitor content strategies and performance',
                businessContext: 'Competitive intelligence analysts monitor what content competitors are producing, identify content gaps, and uncover opportunities to differentiate brand positioning through superior or alternative coverage.'
              },
              { 
                id: 'cm-posts-selected', 
                label: '3. Post Scheduler', 
                path: '/cm-posts-selected', 
                icon: CalendarDays,
                description: 'Schedule approved blog posts for publication across owned channels',
                businessContext: 'Content coordinators manage the publication calendar ensuring consistent content cadence, optimal timing, and strategic topic sequencing. Integrates with CMS platforms for automated publishing.'
              },
              { 
                id: 'reddit-subreddit-search', 
                label: 'Reddit Subreddit Search', 
                path: '/reddit-subreddit-search',
                description: 'Discover relevant Reddit communities and trending discussions for content inspiration',
                businessContext: 'Community managers find niche subreddits and popular threads to understand audience pain points, trending topics, and authentic language patterns that inform content creation strategy.'
              }
            ]
          },
          {
            id: 'seo-driven-content',
            label: 'SEO driven content',
            icon: Globe,
            description: 'Keyword-optimized content creation based on search demand',
            businessContext: 'SEO content writers produce articles targeting specific keywords and search intents to capture organic traffic. This data-driven approach prioritizes search volume, ranking opportunity, and conversion potential.',
            children: [
              {
                id: 'seo-machine',
                label: 'SEO Machine',
                path: '/seo-machine',
                icon: Sparkles,
                description: 'AI-powered SEO content creation and optimization',
                businessContext: 'Content creators use SEO Machine to research topics, write SEO-optimized articles, analyze existing content, and optimize for search engines. Automatically saves articles to the Articles system.'
              },
              {
                id: 'content-revamper',
                label: 'Content Revamper',
                path: '/seo-revamb',
                icon: RefreshCw,
                description: 'Audit and revamp existing content for improved SEO and AI overview eligibility',
                businessContext: 'Content teams audit existing articles and pages to identify SEO improvement opportunities, generate AI-powered revamp suggestions, and optimize content for LLM citations and AI overviews.'
              },
              { 
                id: 'getting-top-articles', 
                label: '1. Discover Sources', 
                path: '/getting-top-articles', 
                icon: Globe,
                description: 'Find top-performing content and authoritative sources for target keywords',
                businessContext: 'SEO analysts research what content currently ranks for target keywords to understand quality benchmarks, topic depth, and content formats that Google rewards. Informs content brief requirements.'
              },
              { 
                id: 'getting-proper-structure', 
                label: '2. Suggest Structure', 
                path: '/getting-proper-structure', 
                icon: FileText,
                description: 'AI-recommended content outlines optimized for SEO and user experience',
                businessContext: 'Content strategists receive data-driven article structures including recommended headings, subtopics, and content sections based on top-ranking competitor analysis and search intent patterns.'
              },
              { 
                id: 'content-briefs', 
                label: '3. Create Content Briefs', 
                path: '/content-briefs', 
                icon: FileEdit,
                description: 'Comprehensive writer briefs with keywords, structure, and requirements',
                businessContext: 'Editorial directors create detailed briefs for writers including target keywords, word count, outline, sources, and brand guidelines. Ensures consistency when working with freelancers or distributed teams.'
              }
            ]
          },
          {
            id: 'quality-checker',
            label: 'Quality Checker',
            icon: CheckSquare,
            description: 'Content quality assurance and editorial review workflows',
            businessContext: 'Quality assurance teams enforce brand standards and editorial excellence through systematic review processes before content publication.',
            children: [
              { 
                id: 'cm-quality-checker-outgoing', 
                label: 'Send to be checked', 
                path: '/cm-quality-checker-outgoing', 
                icon: Upload,
                description: 'Submit content for quality review and editorial approval',
                businessContext: 'Writers submit drafts for editorial review, triggering quality checks for grammar, brand voice, SEO optimization, factual accuracy, and compliance with content guidelines.'
              },
              { 
                id: 'cm-quality-checker-incoming', 
                label: 'Review Results', 
                path: '/cm-quality-checker-incoming', 
                icon: CheckSquare,
                description: 'Review quality assessment feedback and approve or request revisions',
                businessContext: 'Editors review automated and manual quality checks, provide feedback to writers, and approve content for publication or request specific improvements before advancing in workflow.'
              }
            ]
          }
        ]
      },
      {
        id: 'short-form-content',
        label: 'Short-Form Content',
        icon: Smartphone,
        description: 'Social media content creation for LinkedIn, Instagram, and other platforms',
        businessContext: 'Social media managers create platform-optimized posts with AI-generated captions aligned to content pillars. Streamlines social content production while maintaining brand consistency and strategic messaging.',
        children: [
          { 
            id: 'training-short-form', 
            label: '1. Training', 
            path: '/training-short-form', 
            icon: Brain,
            description: 'Configure AI training with brand voice examples and social media guidelines',
            businessContext: 'Social media directors train the AI on brand tone, style examples, and platform-specific guidelines to ensure generated content matches the brand\'s unique voice and audience expectations.'
          },
          { 
            id: 'short-form-content-pillars', 
            label: '1. Generate Post Pillars', 
            path: '/short-form-content-pillars', 
            icon: Sparkles,
            description: 'Define content themes and pillars that guide social media strategy',
            businessContext: 'Content strategists establish 3-7 core content pillars representing key brand messages, audience interests, and business objectives. These pillars ensure balanced, strategic social content distribution.'
          },
          { 
            id: 'short-form-content-ideas', 
            label: '2. Generate Posts', 
            path: '/short-form-content-ideas', 
            icon: Lightbulb,
            description: 'AI-generated social post ideas aligned with content pillars and trends',
            businessContext: 'Social media coordinators receive daily post suggestions that balance content pillars, trending topics, and evergreen themes. Reduces content planning time while maintaining strategic alignment.'
          },
          { 
            id: 'short-form-posts-suggested', 
            label: '2.a Review Captions', 
            path: '/short-form-posts-suggested', 
            icon: Eye,
            description: 'Review and edit AI-generated social media captions before approval',
            businessContext: 'Community managers refine AI-generated captions for platform optimization, add hashtags, adjust tone, and ensure alignment with current events and brand positioning before submission for approval.'
          },
          { 
            id: 'content-approval-dashboard', 
            label: '3. Post Approvals', 
            path: '/content-approval-dashboard', 
            icon: CheckSquare,
            description: 'Centralized approval workflow for social media content with stakeholder review',
            businessContext: 'Marketing directors and legal teams review scheduled posts for brand compliance, accuracy, and strategic fit. Supports multi-level approval chains for regulated industries or risk-averse brands.'
          },
          { 
            id: 'sm-posts-selected', 
            label: '4. Post Scheduler', 
            path: '/sm-posts-selected', 
            icon: CalendarDays,
            description: 'Schedule approved social posts across platforms with optimal timing',
            businessContext: 'Social media managers schedule posts at optimal engagement times, balance content mix, and maintain consistent posting cadence across LinkedIn, Instagram, Twitter, and Facebook.'
          },
          { 
            id: 'keydates', 
            label: 'Keydates', 
            path: '/keydates', 
            icon: Calendar,
            description: 'Calendar of holidays, awareness days, and important dates for content planning',
            businessContext: 'Content planners reference key dates, industry events, and cultural moments to create timely, relevant content that capitalizes on trending conversations and seasonal opportunities.'
          }
        ]
      },
      {
        id: 'creatives-art',
        label: 'Creatives (Art)',
        icon: Palette,
        description: 'AI-powered visual content generation and creative asset management',
        businessContext: 'Creative directors and designers generate brand-aligned visual content using AI, reducing production costs and time while maintaining creative quality and brand consistency.',
        children: [
          { 
            id: 'visual-inspiration', 
            label: 'Inspiration', 
            path: '/visual-inspiration', 
            icon: Sparkles,
            description: 'Browse visual references and creative inspiration for design projects',
            businessContext: 'Designers explore curated visual trends, style references, and creative concepts to inform campaign aesthetics and guide AI image generation with strategic creative direction.'
          },
          { 
            id: 'visual-generation', 
            label: 'Generation', 
            path: '/visuals-creation', 
            icon: Palette,
            description: 'Generate custom images and graphics using AI based on text prompts',
            businessContext: 'Marketing teams create unique visuals for social media, blog posts, and ads without stock photos or designer delays. AI generates brand-aligned images from detailed prompts informed by moodboards.'
          },
          { 
            id: 'past-generations', 
            label: 'My Generations', 
            path: '/past-generations', 
            icon: History,
            description: 'Library of previously generated images with prompts for reuse and variation',
            businessContext: 'Creative teams access their generation history to create variations, reuse successful prompts, and maintain a searchable archive of AI-generated assets for future campaigns.'
          },
          { 
            id: 'creative-prompt-library', 
            label: 'Art Prompt Library', 
            path: '/creative-prompt-library', 
            icon: Library,
            description: 'Reusable prompt templates for consistent AI image generation',
            businessContext: 'Design leads create standardized prompts for common visual needs (hero images, product shots, backgrounds) ensuring brand consistency and enabling non-designers to generate quality visuals.'
          }
        ]
      },
      {
        id: 'newsroom',
        label: 'Newsroom',
        icon: Tv,
        description: 'News aggregation, curation, and distribution platform',
        businessContext: 'PR teams and content marketers monitor industry news, curate relevant articles, and distribute branded bulletins to maintain thought leadership and timely audience engagement.',
        children: [
          { 
            id: 'newsroom-dashboard', 
            label: 'Dashboard', 
            path: '/newsroom', 
            icon: BarChart3,
            description: 'Overview of newsroom activity, top stories, and performance metrics',
            businessContext: 'Content directors monitor news curation activity, trending stories, bulletin performance, and distribution reach to optimize their content aggregation and thought leadership strategy.'
          },
          { 
            id: 'newsroom-programs', 
            label: 'Programs', 
            path: '/newsroom/programs', 
            icon: Tv,
            description: 'Manage recurring news shows, series, and content formats',
            businessContext: 'Editorial teams create themed news programs (daily updates, weekly roundups, industry deep-dives) with consistent formatting and scheduling to build audience habits and brand authority.'
          },
          { 
            id: 'newsroom-sources', 
            label: 'RSS Sources', 
            path: '/newsroom/sources', 
            icon: Globe,
            description: 'Configure RSS feeds and news sources for automated content monitoring',
            businessContext: 'Content curators add trusted industry publications, competitor blogs, and news outlets as sources. The system automatically ingests and categorizes new content for review and potential inclusion in bulletins.'
          },
          { 
            id: 'newsroom-collections', 
            label: 'Collections', 
            path: '/newsroom/collections', 
            icon: FolderOpen,
            description: 'Organize curated articles into themed collections and topic clusters',
            businessContext: 'Editors group related articles by topic, campaign, or audience segment to create comprehensive resources and support content marketing initiatives with authoritative third-party validation.'
          },
          { 
            id: 'newsroom-bulletins', 
            label: 'Bulletins', 
            path: '/newsroom/bulletins', 
            icon: FileText,
            description: 'Create and publish news bulletins and industry roundup emails',
            businessContext: 'Marketing teams compile curated news into branded bulletins with commentary, positioning the brand as an industry thought leader and providing subscriber value through relevant news aggregation.'
          },
          { 
            id: 'newsroom-sponsorships', 
            label: 'Sponsorships', 
            path: '/newsroom/sponsorships', 
            icon: Megaphone,
            description: 'Manage sponsored content placements and partnerships in bulletins',
            businessContext: 'Business development teams monetize newsroom content by offering sponsors featured placement in bulletins, tracking sponsorship value and managing advertiser relationships.'
          }
        ]
      },
      {
        id: 'newsfeed',
        label: 'Newsfeed',
        icon: Rss,
        description: 'AI-curated daily news digest from RSS sources',
        businessContext: 'Marketing teams collect articles from RSS feeds and use AI to generate curated daily news digests, maintaining thought leadership and timely audience engagement with minimal manual effort.',
        children: [
          { 
            id: 'newsfeed-dashboard', 
            label: 'Dashboard', 
            path: '/newsfeed', 
            icon: BarChart3,
            description: 'Overview of newsfeed activity and quick access to features',
            businessContext: 'Content directors get a quick overview of newsfeed collections, generated feeds, and system status.'
          },
          { 
            id: 'newsfeed-sources', 
            label: 'RSS Sources', 
            path: '/newsfeed/sources', 
            icon: Globe,
            description: 'Manage RSS feeds and news sources',
            businessContext: 'Content curators configure trusted RSS feeds for automated daily article collection.'
          },
          { 
            id: 'newsfeed-collections', 
            label: 'Collections', 
            path: '/newsfeed/collections', 
            icon: FolderOpen,
            description: 'View collected articles organized by date',
            businessContext: 'Editors review raw article collections before AI curation to ensure quality sources.'
          },
          { 
            id: 'newsfeed-feeds', 
            label: 'Generated Feeds', 
            path: '/newsfeed/feeds', 
            icon: FileText,
            description: 'View AI-curated daily news digests',
            businessContext: 'Marketing teams review and distribute AI-generated news feeds with minimal editing required.'
          },
          { 
            id: 'newsfeed-threads', 
            label: 'Threads', 
            path: '/newsfeed/threads', 
            icon: Waypoints,
            description: 'Manage automated newsfeed collection and generation schedules',
            businessContext: 'Content teams configure automated workflows that collect RSS articles on schedule and generate AI-curated feeds, reducing manual effort while maintaining consistent content delivery.'
          },
          { 
            id: 'newsfeed-leads', 
            label: 'Subscribers', 
            path: '/newsfeed/leads', 
            icon: Users,
            description: 'Manage newsletter subscribers and their preferences',
            businessContext: 'Marketing teams manage email subscribers who receive curated newsfeed digests, tracking subscriptions and preferences.'
          },
          { 
            id: 'newsfeed-email-analytics', 
            label: 'Email Analytics', 
            path: '/newsfeed/email-analytics', 
            icon: Mail,
            description: 'Newsfeed email queue and engagement',
            businessContext: 'Monitor os_email_queue: queue, delivery, opens, clicks for newsfeed digests.'
          }
        ]
      },
      {
        id: 'avatar-announcements',
        label: 'Avatar Announcements',
        icon: ScanFace,
        description: 'Create AI avatar video announcements with customizable voices',
        businessContext: 'Marketing teams create professional video announcements using AI avatars for social media, internal communications, product launches, and brand messaging without video production costs.',
        children: [
          { 
            id: 'avatar-announcements-list', 
            label: 'Announcements', 
            path: '/avatar-announcements', 
            icon: ScanFace,
            description: 'View and manage all avatar video announcements',
            businessContext: 'Marketing teams view, edit, and organize their library of AI avatar video announcements for quick access and reuse across campaigns.'
          }
        ]
      },
      {
        id: 'press-releases',
        label: 'Press Releases',
        icon: Rss,
        description: 'Press release creation, distribution, and media contact management',
        businessContext: 'PR professionals manage the full press release lifecycle from drafting to targeted distribution to journalists and media outlets, building brand visibility and media relationships.',
        children: [
          { 
            id: 'press-releases', 
            label: 'Press Releases', 
            path: '/press-releases', 
            icon: Rss,
            description: 'Create, edit, and manage company press releases and announcements',
            businessContext: 'PR managers draft press releases for product launches, company milestones, and news announcements following AP style guidelines. AI assists with headline optimization and boilerplate generation.'
          },
          { 
            id: 'dispatch-center', 
            label: 'Dispatch Center', 
            path: '/newsroom/dispatch-center', 
            icon: Send,
            description: 'Send press releases to journalists and media outlets',
            businessContext: 'Communications directors distribute releases to targeted media lists with personalized pitches, track opens and engagement, and manage follow-up sequences to maximize coverage opportunities.'
          },
          { 
            id: 'journalists', 
            label: 'Journalists', 
            path: '/journalists', 
            icon: UsersRound,
            description: 'Database of media contacts with beat coverage and relationship history',
            businessContext: 'PR teams maintain journalist relationships including coverage areas, contact preferences, past interactions, and pitch success rates to improve targeting and increase coverage success.'
          },
          { 
            id: 'distribution-lists', 
            label: 'Distribution Lists', 
            path: '/newsroom/distribution-lists', 
            icon: Users,
            description: 'Segmented media lists for targeted press release distribution',
            businessContext: 'PR coordinators create distribution lists by industry beat, publication tier, geographic region, or topic relevance to ensure press releases reach the most appropriate journalists.'
          },
          { 
            id: 'press-release-campaigns', 
            label: 'Campaigns', 
            path: '/press-releases/campaigns', 
            icon: Mail,
            description: 'Multi-step email sequences for journalist outreach',
            businessContext: 'PR teams create automated follow-up sequences for press release outreach to maximize journalist engagement and coverage opportunities.'
          },
          { 
            id: 'press-release-email-analytics', 
            label: 'Email Analytics', 
            path: '/newsroom/press-releases/email-analytics', 
            icon: BarChart3,
            description: 'Track email performance for press release dispatches',
            businessContext: 'PR teams monitor delivery rates, opens, and engagement for dispatched press releases to measure outreach effectiveness and optimize future communications.'
          }
        ]
      },
      {
        id: 'podcasts',
        label: 'Podcasts',
        icon: Mic,
        description: 'AI-powered podcast script generation and audio content library',
        businessContext: 'Content creators generate podcast scripts and audio content using AI to scale audio content production without hiring voice talent or investing in recording infrastructure.',
        children: [
          { 
            id: 'podcast-generations', 
            label: 'My Podcasts', 
            path: '/podcast-generations', 
            icon: History,
            description: 'Library of previously generated podcast episodes and audio files',
            businessContext: 'Content managers access their podcast library to track published episodes, repurpose content, and maintain a searchable archive of audio assets for distribution and promotion.'
          },
          { 
            id: 'podcast-generation', 
            label: 'Generation', 
            path: '/podcast-generation', 
            icon: Mic,
            description: 'Generate podcast scripts and audio using AI voice synthesis',
            businessContext: 'Marketing teams create podcast episodes from blog content, industry news, or original scripts. AI converts text to natural-sounding audio, enabling podcast presence without recording equipment or hosts.'
          },
          { 
            id: 'podcast-library', 
            label: 'Podcast Templates', 
            path: '/podcast-library', 
            icon: Library,
            description: 'Reusable podcast formats, intro/outro templates, and show structures',
            businessContext: 'Podcast producers create standardized episode formats (interview style, news roundup, solo commentary) with saved intro/outro scripts and segment structures for consistent show branding.'
          }
        ]
      },
      {
        id: 'interactive-polls',
        label: 'Polls',
        icon: BarChart2,
        description: 'Interactive poll creation and audience engagement tracking',
        businessContext: 'Social media marketers and community managers create engaging polls to boost audience interaction, gather feedback, and generate user-generated insights while increasing content engagement rates.',
        children: [
          { 
            id: 'polls', 
            label: 'My Polls', 
            path: '/polls',
            icon: BarChart2,
            description: 'Design and publish interactive polls for social media and websites',
            businessContext: 'Engagement specialists create polls with custom questions, answer options, and visual styling to spark conversations, gather opinions, and increase time-on-site and social engagement metrics.'
          },
          { 
            id: 'poll-sources', 
            label: 'Poll Sources', 
            path: '/poll-sources', 
            icon: FileText,
            description: 'Import poll questions from content, trends, or audience feedback',
            businessContext: 'Community managers source poll ideas from trending topics, customer questions, industry debates, or content themes to ensure polls are relevant and timely for maximum participation.'
          },
          { 
            id: 'poll-performance', 
            label: 'Poll Performance', 
            path: '/poll-performance',
            icon: TrendingUp,
            description: 'Analytics on poll engagement, completion rates, and audience insights',
            businessContext: 'Marketing analysts track which poll topics drive highest engagement, analyze audience sentiment patterns, and use response data to inform content strategy and product decisions.'
          },
          { 
            id: 'poll-styles', 
            label: 'Poll Styles', 
            path: '/poll-styles', 
            icon: Palette,
            description: 'Customize poll visual design to match brand aesthetics',
            businessContext: 'Brand designers create branded poll templates with colors, fonts, and visual elements that maintain brand consistency across interactive content experiences.'
          },
          { 
            id: 'poll-templates', 
            label: 'Poll Templates', 
            path: '/poll-templates', 
            icon: Library,
            description: 'Saved poll formats for recurring engagement campaigns',
            businessContext: 'Social media managers save successful poll formats (weekly industry pulse checks, product feedback, trend predictions) for recurring use to build audience anticipation and routine engagement.'
          }
        ]
      },
      {
        id: 'lead-magnets',
        label: 'Lead Magnets',
        icon: Sparkles,
        description: 'Generate presentations and documents using Gamma AI',
        businessContext: 'Marketing teams create high-value lead magnets (presentations, ebooks, guides) from existing content to attract and capture leads. AI automates the design and formatting process using Gamma to produce professional materials quickly.',
        children: [
          { 
            id: 'lead-magnets', 
            label: 'Create Lead Magnet', 
            path: '/lead-magnets', 
            icon: Sparkles,
            description: 'Generate professional presentations and documents from your content',
            businessContext: 'Content marketers transform blog posts, tips, and documentation into polished Gamma presentations and documents for lead generation campaigns, sales enablement, and educational resources.'
          }
        ]
      },
      {
        id: 'interactive-quizzes',
        label: 'Quizzes',
        icon: ClipboardList,
        description: 'Interactive quiz creation for lead generation and audience engagement',
        businessContext: 'Growth marketers create assessments, knowledge tests, and personality quizzes to generate leads, qualify prospects, and create viral shareable content that drives brand awareness.',
        children: [
          { 
            id: 'quizzes', 
            label: 'My Quizzes', 
            path: '/quizzes', 
            icon: ClipboardList,
            description: 'Design multi-question quizzes with scoring and result segments',
            businessContext: 'Demand generation teams build quizzes with logic branches, scoring systems, and personalized results that capture emails while providing value through self-assessment or entertainment.'
          },
          { 
            id: 'quiz-sources', 
            label: 'Quiz Sources', 
            path: '/quiz-sources', 
            icon: FileText,
            description: 'Generate quiz questions from content, industry knowledge, or trends',
            businessContext: 'Content strategists source quiz questions from existing blog content, industry expertise, or trending topics to create educational or entertaining quizzes aligned with content marketing goals.'
          },
          { 
            id: 'quiz-performance', 
            label: 'Quiz Performance', 
            path: '/quiz-performance', 
            icon: TrendingUp,
            description: 'Track quiz completion rates, lead capture, and sharing metrics',
            businessContext: 'Marketing analysts measure quiz effectiveness through completion rates, social shares, lead quality scores, and conversion data to optimize quiz formats and topics for maximum ROI.'
          },
          { 
            id: 'quiz-styles', 
            label: 'Quiz Styles', 
            path: '/quiz-styles', 
            icon: Palette,
            description: 'Branded quiz designs and visual templates',
            businessContext: 'UX designers create quiz visual themes that balance brand aesthetics with engagement optimization, testing layouts and interactions that maximize completion rates.'
          },
          { 
            id: 'quiz-templates', 
            label: 'Quiz Templates', 
            path: '/quiz-templates', 
            icon: Library,
            description: 'Reusable quiz formats and question structures',
            businessContext: 'Marketing teams save proven quiz frameworks (industry knowledge assessment, product recommender, ROI calculator) for quick deployment in campaigns and lead generation initiatives.'
          }
        ]
      },
      {
        id: 'interactive-mini-games',
        label: 'Mini Games',
        icon: Gamepad2,
        description: 'Interactive games for brand engagement and viral marketing',
        businessContext: 'Creative marketers develop simple browser games and interactive experiences to drive brand awareness, increase session duration, and create memorable brand interactions that encourage sharing.',
        children: [
          { 
            id: 'mini-games', 
            label: 'My Mini Games', 
            path: '/mini-games', 
            icon: Gamepad2,
            description: 'Build simple interactive games for brand engagement campaigns',
            businessContext: 'Brand experience designers create lightweight games (memory match, word puzzles, trivia) that entertain audiences while reinforcing brand messages and collecting engagement data.'
          },
          { 
            id: 'mini-game-templates', 
            label: 'Mini Game Templates', 
            path: '/mini-game-templates', 
            icon: Library,
            description: 'Pre-built game formats ready for brand customization',
            businessContext: 'Campaign managers select from proven game templates and customize with brand visuals, messaging, and prizes to quickly launch engaging interactive campaigns without development resources.'
          },
          { 
            id: 'mini-game-sources', 
            label: 'Mini Game Sources', 
            path: '/mini-game-sources', 
            icon: FileText,
            description: 'Content sources for game questions, challenges, and themes',
            businessContext: 'Content teams pull game content from product knowledge, industry trivia, brand history, or trending topics to create games that educate while entertaining audiences.'
          },
          { 
            id: 'mini-game-performance', 
            label: 'Mini Game Performance', 
            path: '/mini-game-performance', 
            icon: TrendingUp,
            description: 'Analytics on game plays, completion rates, and viral sharing',
            businessContext: 'Digital marketers track game engagement metrics, average play time, completion rates, and social shares to measure campaign success and optimize game mechanics for future initiatives.'
          },
          { 
            id: 'mini-game-types', 
            label: 'Mini Game Types', 
            path: '/mini-game-types', 
            icon: Cog,
            description: 'Manage available game mechanics and interaction types',
            businessContext: 'Experience designers configure which game types (puzzle, trivia, arcade, memory) are available for use, matching game mechanics to brand personality and campaign objectives.'
          }
        ]
      },
      { 
        id: 'content-calendar', 
        label: 'Content Calendar', 
        path: '/content-calendar', 
        icon: CalendarDays,
        description: 'Unified calendar view of all content across channels and formats',
        businessContext: 'Content directors visualize the complete content pipeline across blogs, social media, email, newsroom, and interactive content to ensure balanced coverage, avoid topic fatigue, and identify content gaps.'
      }
    ]
  },
  {
    id: 'badges',
    label: 'Badges',
    icon: BadgeDollarSign,
    description: 'Create trackable badge embeds with analytics',
    businessContext: 'Marketing teams create embeddable badges that partners and affiliates can place on their websites, tracking views and clicks for performance measurement.',
    children: [
      { 
        id: 'badges-list', 
        label: 'My Badges', 
        path: '/badges', 
        icon: LayoutDashboard,
        description: 'Create and manage badge designs'
      },
      { 
        id: 'badge-users', 
        label: 'Badge Earners', 
        path: '/badges/users', 
        icon: Users,
        description: 'Manage users with embed access'
      },
      { 
        id: 'badge-analytics', 
        label: 'Badge Analytics', 
        path: '/badges/analytics', 
        icon: BarChart,
        description: 'View badge performance metrics'
      }
    ]
  },
  {
    id: 'social-media',
    label: 'Social Listening',
    icon: ScanFace,
    description: 'Monitor and analyze social media performance and competitor activity',
    businessContext: 'Social media managers track brand performance on Instagram and LinkedIn, benchmark against competitors, and optimize content strategy based on engagement patterns and audience insights.',
    children: [
      {
        id: 'instagram',
        label: 'Instagram',
        description: 'Instagram profile analytics and competitive benchmarking',
        businessContext: 'Social media strategists analyze Instagram metrics, content performance, and audience growth to refine visual storytelling and improve engagement rates.',
        children: [
          { 
            id: 'sm-instagram-analysis', 
            label: 'Instagram Analysis', 
            path: '/sm-instagram-analysis', 
            icon: Eye,
            description: 'Detailed analytics of Instagram post performance and audience engagement',
            businessContext: 'Community managers evaluate which content types, posting times, and captions drive highest engagement to optimize Instagram strategy and grow follower base organically.'
          },
          { 
            id: 'sm-instagram-get-feed', 
            label: 'Instagram Processing', 
            path: '/sm-instagram-get-feed', 
            icon: Upload,
            description: 'Import Instagram data for analysis and reporting',
            businessContext: 'Analytics teams ingest Instagram metrics to combine with other channel data for comprehensive social media performance dashboards and cross-platform optimization insights.'
          },
          { 
            id: 'sm-instagram-profile-comparisons', 
            label: 'Profile Comparisons', 
            path: '/sm-instagram-profile-comparisons', 
            icon: BarChart,
            description: 'Competitive benchmarking of Instagram profiles and content strategies',
            businessContext: 'Competitive analysts compare brand Instagram performance against key competitors to identify content gaps, engagement tactics, and growth strategies that can be adapted.'
          }
        ]
      },
      {
        id: 'linkedin',
        label: 'LinkedIn',
        description: 'LinkedIn content strategy and professional audience engagement',
        businessContext: 'B2B marketers optimize LinkedIn presence for thought leadership, professional networking, and lead generation through strategic content distribution.',
        children: [
          { 
            id: 'training-short-form', 
            label: '1. Training', 
            path: '/training-short-form', 
            icon: Brain,
            description: 'Configure AI with LinkedIn-specific brand voice and professional tone',
            businessContext: 'LinkedIn strategists train AI on company thought leadership style, professional vocabulary, and B2B audience expectations to generate appropriate content for the platform.'
          },
          { 
            id: 'short-form-content-pillars', 
            label: '1. Generate Post Pillars', 
            path: '/short-form-content-pillars', 
            icon: Sparkles,
            description: 'Define core themes for LinkedIn thought leadership content',
            businessContext: 'Content directors establish expertise pillars that position brand leaders as industry authorities while supporting business development and recruitment goals.'
          },
          { 
            id: 'short-form-content-ideas', 
            label: '2. Generate Posts', 
            path: '/short-form-content-ideas', 
            icon: Lightbulb,
            description: 'AI-generated LinkedIn post ideas aligned with professional content pillars',
            businessContext: 'Marketing coordinators receive daily LinkedIn post suggestions that balance industry insights, company news, and engagement-driving content appropriate for professional audiences.'
          },
          { 
            id: 'short-form-posts-suggested', 
            label: '2.a Review Captions', 
            path: '/short-form-posts-suggested', 
            icon: Eye,
            description: 'Review and refine AI-generated LinkedIn posts',
            businessContext: 'Communications managers edit AI drafts for professional tone, add relevant hashtags, tag connections, and ensure posts align with current business objectives and industry conversations.'
          },
          { 
            id: 'content-approval-dashboard', 
            label: '3. Post Approvals', 
            path: '/content-approval-dashboard', 
            icon: CheckSquare,
            description: 'Executive approval workflow for LinkedIn posts representing company voice',
            businessContext: 'Senior leadership reviews posts from company LinkedIn page ensuring messages align with corporate positioning, avoiding reputational risks while maintaining authentic executive voice.'
          },
          { 
            id: 'sm-posts-selected', 
            label: '4. Post Scheduler', 
            path: '/sm-posts-selected', 
            icon: CalendarDays,
            description: 'Schedule approved LinkedIn posts for optimal professional engagement',
            businessContext: 'Social media managers publish LinkedIn content during business hours when professional audiences are most active, maintaining consistent presence for algorithm favorability.'
          },
          { 
            id: 'keydates', 
            label: 'Keydates', 
            path: '/keydates', 
            icon: Calendar,
            description: 'Industry events and professional milestones for LinkedIn content planning',
            businessContext: 'B2B marketers reference industry conferences, awareness days, and professional milestones to create timely LinkedIn content that participates in relevant business conversations.'
          }
        ]
      },
      { 
        id: 'sm-competitors-list', 
        label: 'SM Competitors List', 
        path: '/sm-competitors-list', 
        icon: Users,
        description: 'Manage list of competitor social media profiles to monitor',
        businessContext: 'Competitive intelligence teams maintain watchlists of competitor social accounts to track their content strategies, engagement tactics, and audience growth for strategic insights.'
      },
      { 
        id: 'social-media-channel-suggestion', 
        label: 'Channel Mix', 
        path: '/social-media-channel-suggestion', 
        icon: Target,
        description: 'AI recommendations for optimal social media platform allocation',
        businessContext: 'Marketing strategists receive data-driven guidance on which social platforms to prioritize based on audience demographics, content types, and business objectives to focus resources effectively.'
      },
      { 
        id: 'social-media-settings', 
        label: 'Settings', 
        path: '/social-media-settings', 
        icon: Settings,
        description: 'Configure social media integrations and monitoring parameters',
        businessContext: 'Social media managers connect platform APIs, set monitoring keywords, define alert thresholds, and customize dashboards to track metrics most relevant to business goals.'
      }
    ]
  },
  {
    id: 'seo',
    label: 'SEO',
    icon: FolderSearch,
    description: 'Search engine optimization tools and analytics across all SEO disciplines',
    businessContext: 'SEO specialists optimize organic search visibility through keyword research, technical audits, content optimization, and competitive analysis to drive qualified traffic and improve search rankings.',
    children: [
      { 
        id: 'seo-dashboard', 
        label: 'Dashboard', 
        path: '/seo-dashboard', 
        icon: Lightbulb,
        description: 'Centralized SEO performance overview with key metrics and priorities',
        businessContext: 'SEO managers monitor organic traffic trends, ranking movements, and priority optimization tasks in one view to identify opportunities and track progress toward visibility goals.'
      },
      { 
        id: 'seo-testing', 
        label: 'SEO Testing Suite', 
        path: '/seo-testing', 
        icon: FlaskConical,
        description: 'Test and validate SEO implementations before deploying changes',
        businessContext: 'Technical SEO specialists validate meta tags, structured data, canonical tags, and technical elements in a safe environment before pushing to production to avoid ranking drops.'
      },
      { 
        id: 'seo-diagnostic', 
        label: 'SEO Diagnostic', 
        path: '/seo-diagnostic', 
        icon: BarChart3,
        description: 'Comprehensive technical SEO audit identifying issues and opportunities',
        businessContext: 'SEO consultants run automated audits to detect crawlability issues, site speed problems, mobile usability errors, and technical barriers preventing optimal search performance.'
      },
      { 
        id: 'seo-trends-analysis', 
        label: 'SEO & Google Trends', 
        path: '/seo-trends-analysis', 
        icon: TrendingUp,
        description: 'Identify trending search topics and seasonal search patterns',
        businessContext: 'Content strategists discover rising search trends and seasonal patterns to create timely content that captures emerging demand before competitors saturate the space.'
      },
      { 
        id: 'serps-research', 
        label: 'SERPs Research', 
        path: '/serps-research', 
        icon: TrendingUp,
        description: 'Analyze search engine results pages to understand ranking factors',
        businessContext: 'SEO analysts examine what content types, formats, and features Google displays for target keywords to inform content strategy and feature development.'
      },
      { 
        id: 'ai-overview-research', 
        label: 'AI Overview Research', 
        path: '/ai-overview-research', 
        icon: TrendingUp,
        description: 'Monitor Google AI Overviews and optimize for generative search',
        businessContext: 'Forward-thinking SEOs track which queries trigger AI Overviews, what sources Google cites, and how to position content for visibility in generative search experiences.'
      },
      {
        id: 'keyword-explorer',
        label: 'Keyword Explorer',
        icon: Search,
        description: 'Comprehensive keyword research and opportunity discovery',
        businessContext: 'SEO professionals discover high-value keywords through multiple research methodologies to build content strategies targeting achievable rankings with business impact.',
        children: [
          { 
            id: 'keyword-research', 
            label: 'Keyword Research', 
            path: '/keyword-research', 
            icon: Search,
            description: 'Discover keyword opportunities with search volume and competition data',
            businessContext: 'SEO teams identify keywords with optimal balance of search demand, ranking difficulty, and business relevance to prioritize content creation and optimization efforts.'
          },
          { 
            id: 'keyword-hopping', 
            label: 'Keyword Hopping', 
            path: '/keyword-hopping', 
            icon: Target,
            description: 'Find related keyword variations and semantic clusters',
            businessContext: 'Content optimizers expand keyword lists by discovering related terms, questions, and variations that share search intent, enabling comprehensive topic coverage.'
          },
          { 
            id: 'keyword-assistant', 
            label: 'Keyword Assistant', 
            path: '/keyword-assistant', 
            icon: Wand2,
            description: 'AI-powered keyword suggestions and search intent analysis',
            businessContext: 'SEO writers receive AI guidance on which keywords to target in content, optimal placement strategies, and semantic terms that strengthen topical relevance signals.'
          },
          { 
            id: 'keywords-5ws', 
            label: 'Keywords (5Ws Framework)', 
            path: '/keywords-5ws', 
            icon: Brain,
            description: 'Organize keywords by who, what, when, where, why search intents',
            businessContext: 'Content strategists structure keyword research around the 5Ws framework to ensure content addresses all angles of user questions and covers topics comprehensively.'
          },
          { 
            id: 'my-runs', 
            label: 'My runs', 
            path: '/my-runs', 
            icon: ListChecks,
            description: 'Track batch analysis runs (5Ws, etc.) and view job progress',
            businessContext: 'Users monitor long-running batch jobs started from Keywords 5Ws or other features; they can leave the page and return to see progress and results.'
          },
          { 
            id: 'competitors-keywords', 
            label: 'Keywords of Competitors', 
            path: '/competitors-keywords', 
            icon: Target,
            description: 'Discover keywords competitors rank for that you don\'t',
            businessContext: 'Competitive SEO analysts identify keyword gaps where competitors capture traffic, revealing content opportunities and underserved market segments to target.'
          }
        ]
      },
      {
        id: 'on-site-seo',
        label: 'On-Site SEO',
        icon: Target,
        description: 'Site-wide SEO analysis and strategic optimization planning',
        businessContext: 'Enterprise SEO teams analyze entire site architecture, internal linking, and historical performance to build comprehensive optimization roadmaps.',
        children: [
          { id: '3danalysis', label: '3D Analysis', path: '/3danalysis_step1', icon: Microscope, description: 'Comprehensive SEO analysis with data collection, opportunity detection, categorization, and strategy', businessContext: 'SEO analysts gather baseline data on current rankings, traffic, and technical health, then identify optimization opportunities.' },
          { id: 'historical-declines', label: '3D Analysis - Historical Declines', path: '/historical-declines', icon: TrendingDown, description: 'Identify pages with traffic drops requiring recovery', businessContext: 'SEO specialists pinpoint content that lost rankings to prioritize recovery efforts.' },
          { id: 'seo-selection-groups', label: 'Focus Keywords Groups', path: '/seo-selection-groups', icon: CheckSquare, description: 'Organize target keywords into strategic optimization groups', businessContext: 'SEO managers cluster keywords by topic and priority for systematic optimization campaigns.' },
          { id: 'branded-keywords', label: 'Branded Keywords Classification', path: '/branded-keywords', icon: Tag, description: 'Separate branded from non-branded keyword performance', businessContext: 'Marketing analysts distinguish brand awareness traffic from demand capture to properly attribute marketing channel effectiveness.' },
          { id: 'seo-internal-linking', label: 'Internal Linking Analysis', path: '/seo/internal-linking', icon: LinkIcon, description: 'Optimize internal link structure for crawlability and authority flow', businessContext: 'Technical SEOs improve PageRank distribution and crawl efficiency through strategic internal linking patterns.' },
          { id: 'seo-ai-insights', label: 'AI Insights', path: '/seo/ai-insights', icon: Sparkles, description: 'AI-powered SEO strategy assistant and insights', businessContext: 'SEO managers get personalized recommendations and strategic guidance based on their keyword-page matching data and performance metrics.' },
          { id: 'seo-pages', label: 'Page Analysis', path: '/seo/pages', icon: FileText, description: 'Analyze page performance scores and ranking queries', businessContext: 'SEO analysts review page-level performance metrics, identify optimization opportunities, and track ranking queries for each page.' },
          { id: 'seo-cannibalization', label: 'Cannibalization Detection', path: '/seo/cannibalization', icon: AlertTriangle, description: 'Identify and resolve queries ranking on multiple pages', businessContext: 'SEO specialists detect keyword cannibalization issues where the same query ranks on multiple pages, then consolidate content with 301 redirects to improve rankings.' },
          { id: 'seo-content-intelligence', label: 'Content Intelligence', path: '/seo/content-intelligence', icon: Brain, description: 'Scrape, analyze, and regenerate content with AI-powered insights', businessContext: 'Content SEOs use Firecrawl to scrape competitor content, analyze SERP results, and regenerate optimized content using AI to outrank competitors.' },
          { id: 'seo-analytics', label: 'Analytics Dashboard', path: '/seo/analytics', icon: BarChart3, description: 'Overview of keyword-page matching performance metrics', businessContext: 'SEO managers review aggregated performance metrics, top performing keywords and pages, and track overall SEO health from their keyword-page matching data.' },
          { id: 'seo-keywords', label: 'Keywords Analysis', path: '/seo/keywords', icon: Search, description: 'Analyze search keywords and discover optimization opportunities', businessContext: 'SEO analysts review keyword performance, identify opportunities for improvement, and track which pages rank for each keyword.' },
          { id: 'seo-keyword-clusters', label: 'Keyword Clusters', path: '/seo/keyword-clusters', icon: Network, description: 'Group and analyze your keywords by topic', businessContext: 'SEO managers automatically group related queries to identify content themes and gaps for comprehensive content creation.' },
          { id: 'seo-pagespeed', label: 'Page Speed', path: '/seo/pagespeed', icon: Zap, description: 'Analyze and monitor your page performance metrics', businessContext: 'Technical SEOs and developers track Core Web Vitals and performance scores over time to identify optimization opportunities and measure improvements.' },
          { id: 'seo-briefs', label: 'Content Briefs', path: '/seo/briefs', icon: FileText, description: 'Generate AI-powered content briefs for your keywords', businessContext: 'Content creators and SEO managers generate comprehensive, SEO-optimized content outlines with competitor analysis and recommendations.' }
        ]
      },
      {
        id: 'on-page-seo',
        label: 'On-Page SEO',
        icon: FileEdit,
        description: 'Page-level content optimization and meta data management',
        businessContext: 'Content SEOs optimize individual pages for target keywords through strategic meta tags, content structure, and on-page elements.',
        children: [
          { id: 'onpage-seo-audit', label: 'OnPage SEO Audit', path: '/onpage-seo-audit', icon: CheckSquare, description: 'Comprehensive page-level SEO quality checks', businessContext: 'SEO editors evaluate pages against 100+ ranking factors to identify quick wins and technical issues.' },
          { id: 'meta-generator', label: 'Meta Generator', path: '/meta-generator', icon: FileText, description: 'AI-powered title tags and meta descriptions', businessContext: 'Content teams generate click-optimized meta tags that improve CTR while incorporating target keywords naturally.' },
          { id: 'faq-generator', label: 'FAQ Generator', path: '/faq-generator', icon: MessageSquare, description: 'Create FAQ schema markup content for featured snippets', businessContext: 'SEO writers generate FAQ sections targeting People Also Ask boxes to capture SERP real estate and voice search queries.' }
        ]
      },
      {
        id: 'off-page-seo',
        label: 'Off-Page SEO',
        icon: Globe,
        description: 'Backlink analysis and external authority building',
        businessContext: 'Link building specialists analyze competitor backlink profiles to identify high-authority link opportunities.',
        children: [
          { id: 'competitors-backlinks', label: 'Competitors Backlinks', path: '/competitors-backlinks', icon: Link2, description: 'Analyze competitor backlink profiles and opportunities', businessContext: 'SEO outreach teams discover where competitors earn links to replicate their link building strategies and find new prospects.' },
          { id: 'backlink-intelligence', label: 'Backlink Intelligence', path: '/seo/backlink-intelligence', icon: BarChart3, description: 'Track which backlinks drive real traffic and influence keyword rankings', businessContext: 'SEO analysts correlate referral traffic with organic performance to identify high-value backlinks that impact search rankings.' }
        ]
      },
      {
        id: 'youtube-seo',
        label: 'YouTube SEO',
        icon: Youtube,
        description: 'Video content optimization for YouTube search and discovery',
        businessContext: 'Video marketers optimize video titles, descriptions, and tags to improve YouTube search rankings and recommendation algorithm performance.',
        children: [
          { id: 'youtube-seo', label: 'Overview', path: '/youtube-seo', icon: Youtube, description: 'YouTube channel performance and optimization dashboard', businessContext: 'Video strategists track channel metrics and identify optimization opportunities.' },
          { id: 'youtube-seo-keywords', label: 'Keywords', path: '/youtube-seo-keywords', icon: Search, description: 'Discover YouTube search keywords and video topics', businessContext: 'Content creators find what people search on YouTube to guide video topic selection.' },
          { id: 'youtube-seo-suggestions', label: 'Suggestions', path: '/youtube-seo-suggestions', icon: Lightbulb, description: 'AI recommendations for video optimization', businessContext: 'Video editors receive title, description, and thumbnail improvement suggestions.' }
        ]
      },
      {
        id: 'local-seo',
        label: 'Local SEO',
        icon: MapPin,
        description: 'Local business search optimization and Google Business Profile management',
        businessContext: 'Local businesses optimize for map pack rankings and location-based searches to drive foot traffic and local conversions.',
        children: [
          { id: 'local-seo', label: 'Local SEO', path: '/local-seo', icon: MapPin, description: 'Manage local listings and Google Business Profile optimization', businessContext: 'Multi-location businesses optimize NAP consistency, reviews, and local content.' }
        ]
      },
      {
        id: 'appstore-seo',
        label: 'AppStore SEO (ASO)',
        icon: Smartphone,
        description: 'Mobile app store search optimization for iOS and Android',
        businessContext: 'App marketers optimize app store presence to improve search rankings and increase organic app downloads.',
        children: [
          { id: 'appstore-seo', label: 'Overview', path: '/appstore-seo', icon: Smartphone, description: 'App store performance dashboard and ASO metrics', businessContext: 'Mobile product managers track app visibility, keyword rankings, and conversion rates.' },
          { id: 'appstore-seo-keywords', label: 'Keywords', path: '/appstore-seo-keywords', icon: Search, description: 'App store keyword research and competitor analysis', businessContext: 'ASO specialists identify keywords users search to discover apps in their category.' },
          { id: 'appstore-seo-suggestions', label: 'Suggestions', path: '/appstore-seo-suggestions', icon: Lightbulb, description: 'AI-powered app listing optimization recommendations', businessContext: 'App marketers improve titles, descriptions, and screenshots for higher conversion.' }
        ]
      },
      {
        id: 'ecommerce-seo',
        label: 'E-commerce SEO',
        icon: ShoppingCart,
        description: 'Product page and category optimization for online stores',
        businessContext: 'E-commerce SEOs optimize product descriptions, category pages, and technical elements to capture high-intent commercial searches.',
        children: [
        { id: 'ecommerce-seo', label: 'E-commerce SEO', path: '/ecommerce-seo', icon: ShoppingCart, description: 'Online store SEO optimization and product page analysis', businessContext: 'E-commerce managers optimize product catalogs for transactional keywords and shopping queries.' }
        ]
      }
    ]
  },
  {
    id: 'saio',
    label: 'GEO (aka SAIO)',
    icon: BrainCircuit,
    description: 'Generative Engine Optimization for AI search and LLM visibility',
    businessContext: 'Forward-thinking SEO teams optimize content for AI-powered search engines like ChatGPT, Perplexity, and Google SGE to ensure brand visibility in generative AI responses.',
    children: [
      { id: 'saio-dashboard', label: 'Dashboard', path: '/saio-dashboard', icon: Lightbulb, description: 'SAIO performance tracking and optimization overview', businessContext: 'SEO strategists monitor how often brand content appears in AI search results and track citation frequency.' },
      { id: 'prompts', label: 'Prompts', path: '/prompts', icon: MessageSquare, description: 'Test queries to monitor brand mentions in AI responses', businessContext: 'Content teams track which prompts trigger brand mentions to inform content optimization strategy.' },
      { id: 'run-analysis', label: 'Run Analysis', path: '/run-analysis', icon: Play, description: 'Execute SAIO audits across multiple AI search engines', businessContext: 'Analysts batch-test brand visibility across ChatGPT, Perplexity, Gemini, and other LLMs.' },
      { id: 'scheduler', label: 'Schedule Management', path: '/scheduler', icon: Clock, description: 'Automate recurring SAIO monitoring tasks', businessContext: 'SEO teams schedule regular checks to track changes in AI search visibility over time.' },
      { id: 'dynamic-competitor-classification', label: 'Competitor Classification', path: '/dynamic-competitor-classification', icon: Brain, description: 'Analyze which competitors AI engines cite for target topics', businessContext: 'Competitive analysts identify which brands AI tools reference to understand competitive positioning.' },
      { id: 'dynamic-people-classification', label: 'People Classification', path: '/dynamic-people-classification', icon: Users, description: 'Track thought leader mentions in AI search results', businessContext: 'Personal brand managers monitor when AI cites company executives or team members as authorities.' },
      { id: 'ai-search-optimization-framework', label: 'AI Search Optimization', path: '/ai-search-optimization-framework', icon: Target, description: 'Strategic framework for optimizing content for AI visibility', businessContext: 'SEO directors develop systematic approaches to earning AI citations and improving generative search presence.' },
      { id: 'saio-data-exploration-tool', label: 'Data Exploration Tool', path: '/saio-data-exploration-tool', icon: Database, description: 'Analyze patterns in AI search results data', businessContext: 'Data scientists explore SAIO performance data to identify content patterns that earn more AI citations.' },
      { id: 'ai-overview-research', label: 'AI Overview Research', path: '/ai-overview-research', icon: BrainCircuit, description: 'Monitor Google AI Overview appearances', businessContext: 'SEO specialists track which queries trigger AI Overviews and how to optimize for inclusion.' }
    ]
  },
  {
    id: 'cla',
    label: 'Competitive Analysis',
    icon: Crosshair,
    description: 'Competitive Landscape Analysis for comprehensive competitor intelligence',
    businessContext: 'Strategy teams analyze competitors\' SEO rankings, content strategies, and AI/LLM visibility to generate comprehensive competitive intelligence reports for clients.',
    children: [
      { 
        id: 'cla-dashboard', 
        label: 'Dashboard', 
        path: '/cla', 
        icon: BarChart3,
        description: 'Overview of all competitive analysis projects',
        businessContext: 'View and manage all CLA projects, track analysis status, and access completed reports.'
      }
    ]
  },
  {
    id: 'cro-ux',
    label: 'CRO/UX',
    icon: Crosshair,
    description: 'Conversion rate optimization and user experience improvements',
    businessContext: 'CRO specialists analyze user behavior and test improvements to increase conversion rates, reduce friction, and optimize the customer journey.',
    children: [
      { id: 'cro-dashboard', label: 'Dashboard', path: '/cro', icon: BarChart3, description: 'Conversion funnel metrics and optimization opportunities', businessContext: 'Growth teams monitor conversion rates, drop-off points, and experiment performance to prioritize UX improvements.' },
      { id: 'cro-website-suggestions', label: 'Website Suggestions', path: '/cro/website-suggestions', icon: Target, description: 'AI-powered recommendations for conversion improvements', businessContext: 'UX designers receive data-driven suggestions for layout changes, copy improvements, and friction reduction that increase conversions.' }
    ]
  },
  {
    id: 'fact-checker',
    label: 'Fact Checker',
    icon: ShieldCheck,
    description: 'AI-powered video claim verification and analysis',
    businessContext: 'Content teams and fact-checkers analyze video content to identify and verify factual claims, detecting misinformation and ensuring content accuracy before sharing or responding to competitive content.',
    children: [
      {
        id: 'fact-checker-analyze',
        label: 'Analyze Video',
        path: '/fact-checker',
        icon: ShieldCheck,
        description: 'Analyze YouTube videos to detect and verify factual claims',
        businessContext: 'Content teams submit YouTube videos for AI-powered analysis that extracts claims, verifies facts against reliable sources, and identifies potential misinformation.'
      },
      {
        id: 'my-fact-checks',
        label: 'My Fact Checks',
        path: '/my-fact-checks',
        icon: FileText,
        description: 'View and manage previous fact-check sessions',
        businessContext: 'Users access historical fact-check results, review verified claims, and track analysis outcomes over time for reference and reporting purposes.'
      }
    ]
  },
  {
    id: 'synthetic-personas',
    label: 'Synthetic Personas',
    icon: UserRoundSearch,
    description: 'AI-generated customer personas for research and targeting',
    businessContext: 'Product managers and researchers create realistic synthetic personas to validate strategies, simulate customer feedback, and inform targeting without recruiting real participants.',
    children: [
      { id: 'personas-dashboard', label: 'Dashboard', path: '/personas-dashboard', icon: Lightbulb, description: 'Overview of persona projects and insights', businessContext: 'Research teams monitor persona generation status and key insights.' },
      { id: 'personas-brief-settings', label: 'Settings', path: '/personas-brief-settings', icon: Cog, description: 'Configure persona generation parameters', businessContext: 'Researchers set demographic criteria and behavioral attributes for persona creation.' },
      { id: 'personas-brief-list', label: 'Generate Personas Brief', path: '/personas-brief-list', icon: ClipboardList, description: 'Create detailed persona briefs with attributes', businessContext: 'Marketing strategists define persona requirements for AI generation.' },
      { id: 'my-personas', label: 'Generate Personas', path: '/my-personas', icon: Users, description: 'AI-generate synthetic customer personas', businessContext: 'Teams create realistic personas with backgrounds, motivations, and behaviors.' },
      { id: 'personas', label: 'Personas', path: '/personas', icon: UserSquare, description: 'Library of all generated personas', businessContext: 'Product teams access persona profiles for strategy validation.' },
      { id: 'recruiting-participants', label: 'Recruiting participants', path: '/recruiting-participants', icon: UserCheck, description: 'Find and recruit real research participants', businessContext: 'Researchers recruit actual users to validate synthetic persona findings.' },
      { id: 'participants', label: 'Participants', path: '/participants', icon: Users, description: 'Manage research participant database', businessContext: 'UX researchers maintain participant pool for studies.' }
    ]
  },
  {
    id: 'focus-groups',
    label: 'Focus Groups',
    icon: Focus,
    description: 'AI-simulated focus group discussions for rapid feedback',
    businessContext: 'Product teams run virtual focus groups with synthetic personas to test concepts, messaging, and products before investing in real research.',
    children: [
      { id: 'fg-dashboard', label: 'Dashboard', path: '/fg-dashboard', icon: Lightbulb, description: 'Focus group project overview', businessContext: 'Research leads track ongoing studies and key findings.' },
      { id: 'projects', label: 'Projects', path: '/projects', icon: FolderOpen, description: 'Manage focus group research projects', businessContext: 'Teams organize studies by product, campaign, or research question.' },
      { id: 'preparing-stimuli', label: 'Preparing Stimuli', path: '/preparing-stimuli', icon: Lightbulb, description: 'Create materials to show focus group participants', businessContext: 'Designers prepare concepts, ads, or prototypes for testing.' },
      { id: 'preparing-inputs', label: 'Preparing Inputs', path: '/preparing-inputs', icon: MessageSquare, description: 'Define discussion questions and prompts', businessContext: 'Moderators create question guides for structured feedback.' },
      { id: 'contacting-fg', label: 'Contacting Focus Group', path: '/contacting-fg', icon: Phone, description: 'Initiate AI focus group sessions', businessContext: 'Researchers launch simulated discussions with synthetic personas.' },
      { id: 'fg-results', label: 'Focus Group Results', path: '/fg-results', icon: BarChart, description: 'Analyze focus group feedback and insights', businessContext: 'Product managers review responses to inform decisions.' }
    ]
  },
  {
    id: 'planning-budgeting',
    label: 'Planning & Budgeting',
    icon: CalendarDays,
    description: 'Marketing budget allocation and campaign planning',
    businessContext: 'Marketing directors allocate budgets across products and plan campaign investments to maximize ROI.',
    children: [
      { id: 'product-weights', label: 'Product Weights', path: '/product-weights', icon: Percent, description: 'Set budget allocation percentages by product', businessContext: 'CMOs distribute marketing spend based on product priorities and revenue targets.' },
      { id: 'planning-sheet', label: 'Planning Sheet', path: '/planning-sheet', icon: CalendarDays, description: 'Annual and quarterly marketing calendar planning', businessContext: 'Marketing managers map campaigns, launches, and initiatives across time periods.' }
    ]
  },
  {
    id: 'digital-operations',
    label: 'Tasks',
    icon: Radical,
    description: 'Task management and sprint planning for marketing operations',
    businessContext: 'Marketing ops teams organize work into sprints, track task completion, and manage team workload.',
    children: [
      { id: 'task-planning', label: 'Task Planning (To Sprints)', path: '/task-planning', icon: ClipboardList, description: 'Organize tasks into sprint cycles', businessContext: 'Ops managers prioritize and batch work into 2-week sprints.' },
      { id: 'projects', label: 'Projects', path: '/projects', icon: FolderKanban, description: 'Manage projects and organize tasks by project', businessContext: 'Teams group related tasks into projects for better organization and tracking.' },
      { id: 'tasks', label: 'Tasks in Sprints', path: '/tasks', icon: CheckSquare, description: 'Active sprint task board', businessContext: 'Team members track progress on current sprint commitments.' },
      { id: 'scheduled-tasks', label: 'Recurring Tasks', path: '/scheduled-tasks', icon: CalendarDays, description: 'Automated recurring task templates', businessContext: 'Ops leads create repeating workflows for routine activities.' },
      { id: 'tasks-aging', label: 'Tasks Aging', path: '/tasks-aging', icon: Clock, description: 'Identify overdue and stalled tasks', businessContext: 'Managers spot bottlenecks and reassign blocked work.' },
      { id: 'tasks-journal', label: 'Tasks (Journal/Report)', path: '/tasks-journal', icon: FileText, description: 'Task completion history and reports', businessContext: 'Directors review team productivity and project velocity.' },
      { id: 'tasks-notifications', label: 'Task Notifications', path: '/tasks-notifications', icon: Mail, description: 'Configure task alert preferences', businessContext: 'Team members set reminders for deadlines and assignments.' }
    ]
  },
  {
    id: 'sales-operations',
    label: 'Sales',
    icon: BadgeDollarSign,
    description: 'Sales proposal generation and meeting documentation',
    businessContext: 'Sales teams create proposals, document meetings, and track sales performance metrics.',
    children: [
      { id: 'sales-proposals', label: 'Sales Proposals', path: '/sales-operations/proposals', icon: FileText, description: 'Generate and manage sales proposals', businessContext: 'Account executives create branded proposals with pricing and scope.' },
      { id: 'sales-moms', label: 'Sales MoMs', path: '/sales-operations/moms', icon: FileText, description: 'Meeting minutes and action items', businessContext: 'Sales reps document client meetings and follow-up commitments.' },
      { id: 'msa-templates', label: 'MSA Templates', path: '/sales-operations/msa-templates', icon: FileEdit, description: 'Master Service Agreement templates for client contracts', businessContext: 'Sales teams create and customize MSA templates with dynamic data blocks for proposals, pricing, and payment schedules.' },
      { id: 'msa-documents', label: 'MSA Documents', path: '/sales-operations/msa-documents', icon: FileText, description: 'Generated MSA documents for specific proposals', businessContext: 'Sales teams create final MSA documents from templates, populate them with proposal data, and generate PDFs for client signing.' },
      { id: 'supportive-templates', label: 'Supportive Templates', path: '/sales-operations/supportive-templates', icon: FileEdit, description: 'Supportive document templates for proposals', businessContext: 'Sales teams create and customize supportive document templates like "How We Work Together" to enhance proposals.' },
      { id: 'supportive-documents', label: 'Supportive Documents', path: '/sales-operations/supportive-documents', icon: FileText, description: 'Generated supportive documents for proposals', businessContext: 'Sales teams create and manage supportive documents that can be assigned to multiple proposals.' },
      { id: 'sales-insights', label: 'Sales Insights', path: '/sales-insights', icon: BarChart3, description: 'Sales performance analytics and trends', businessContext: 'Sales directors track win rates, deal velocity, and revenue forecasts.' }
    ]
  },
  {
    id: 'meetings',
    label: 'Meetings',
    icon: CalendarIcon,
    description: 'Structured meeting management with timer, sections, and issues-to-tasks workflow',
    businessContext: 'Teams run structured meetings with check-in, agenda sections, issue tracking, voting, and automatic task creation. Supports brainstorming, project progress, client, and internal meeting types.',
    children: [
      { 
        id: 'meetings-list', 
        label: 'All Meetings', 
        path: '/meetings', 
        icon: CalendarIcon,
        description: 'View and manage all meetings',
        businessContext: 'Meeting organizers view past and upcoming meetings, filter by type and status, and start or edit meetings.'
      },
      { 
        id: 'meetings-vote-now', 
        label: 'Vote Now!', 
        path: '/meetings/vote-now', 
        icon: Vote,
        description: 'Meetings waiting for your vote',
        businessContext: 'Meeting participants see meetings with issues in voting status that require their input.'
      },
      { 
        id: 'meetings-new', 
        label: 'New Meeting', 
        path: '/meetings/new', 
        icon: Plus,
        description: 'Create a new meeting',
        businessContext: 'Team leads create meetings, select participants from growth squad, choose templates, and set agendas.'
      },
      { 
        id: 'meetings-templates', 
        label: 'Templates', 
        path: '/meetings/templates', 
        icon: FileText,
        description: 'Manage meeting templates',
        businessContext: 'Meeting facilitators create reusable templates with predefined sections and fields for consistent meeting structures.'
      }
    ]
  },
  {
    id: 'sops-templates',
    label: 'SOPs',
    icon: Waypoints,
    description: 'Standard operating procedures and process documentation',
    businessContext: 'Operations teams document repeatable processes, best practices, and workflows to ensure consistency and enable team scaling.',
    children: [
      { id: 'sop-dashboard', label: 'Dashboard', path: '/sop-dashboard', icon: Lightbulb, description: 'SOP library overview', businessContext: 'Ops managers track documentation coverage and usage.' },
      { id: 'sops', label: 'SOPs', path: '/sops', icon: FileText, description: 'Create and manage standard operating procedures', businessContext: 'Teams document step-by-step processes for recurring tasks.' },
      { id: 'business-annotations', label: 'Business Annotations', path: '/business-annotations', icon: Tag, description: 'Add context notes to business processes', businessContext: 'Subject matter experts annotate processes with tips and gotchas.' }
    ]
  },
  {
    id: 'reporting-hub',
    label: 'Reports',
    icon: FlagTriangleRight,
    description: 'Marketing performance dashboards and analytics reports',
    businessContext: 'Executives and stakeholders access comprehensive reports on marketing impact, business metrics, and campaign effectiveness.',
    children: [
      { id: 'control-center', label: 'Control Center', path: '/contacts', icon: TowerControl, description: 'Centralized contact management and activity tracking', businessContext: 'Teams manage all contacts across modules with unified filtering, activity classification, and cross-module association tracking.' },
      { id: 'holdco-views', label: 'Consolidated Reports', path: '/holdco-views', icon: Building2, description: 'Create aggregated views across multiple brands with configurable dashboards', businessContext: 'Holdco managers and executives create views to aggregate data from multiple brands they manage. Each view requires brand owner approval via PIN validation, and can optionally grant viewer role access. Dashboards are configurable per view and support both aggregated and brand-specific data views.' },
      { id: 'custom-reports', label: 'Custom Reports', path: '/custom-reports', icon: LayoutDashboard, description: 'Create and share custom dashboards for a brand', businessContext: 'Users create custom reports with selected dashboard components for a single brand, then view or share via public link.' },
      { id: 'business-activity', label: 'Business Activity', path: '/business-activity', icon: Activity, description: 'Daily marketing activity log', businessContext: 'Teams review what was published, sent, and launched.' },
      { id: 'business-metrics', label: 'Business Events', path: '/business-metrics', icon: TrendingUp, description: 'Key business KPIs and trends', businessContext: 'Executives monitor revenue, leads, and growth metrics.' },
      { id: 'email-marketing-report', label: 'Email Marketing Report', path: '/email-marketing-report', icon: Mail, description: 'Email campaign performance analytics', businessContext: 'Email marketers track opens, clicks, and conversions.' },
      { id: 'google-impact', label: 'Google Impact', path: '/google-impact', icon: Globe, description: 'Google Ads and Analytics combined view', businessContext: 'Analysts measure paid and organic Google channel impact.' },
      { id: 'causality-report', label: 'Causality Report', path: '/causality-report', icon: Globe, description: 'Marketing attribution and cause-effect analysis', businessContext: 'Data scientists identify which activities drive business outcomes.' },
      { id: 'seo-analytics', label: 'SEO Analytics', path: '/seo-analytics', icon: BarChart, description: 'Organic search performance metrics', businessContext: 'SEO teams track rankings, traffic, and visibility trends.' },
      { id: 'seo-report', label: 'SEO only Report (GSC)', path: '/seo-report', icon: Search, description: 'Google Search Console data dashboard', businessContext: 'SEO specialists analyze search queries and click patterns.' },
      { id: 'analytics', label: 'Analytics', path: '/analytics', icon: BarChart3, description: 'Comprehensive marketing analytics', businessContext: 'Analysts explore all marketing data in unified dashboards.' },
      { id: 'predictive-analytics', label: 'Predictive Analytics', path: '/predictive-analytics', icon: Brain, description: 'AI-powered forecasts and predictions', businessContext: 'Strategists use ML models to predict future performance.' },
      { id: 'business-on-map', label: 'My Business on a Map', path: '/business-on-map', icon: MapPin, description: 'Visualize business reach across the globe with interactive geo data', businessContext: 'Sales and marketing teams visualize geographic distribution of proposal accesses, client locations, and market penetration to identify expansion opportunities and understand global reach.' }
    ]
  },
  {
    id: 'personal-cards',
    label: 'Personal Cards',
    icon: Contact,
    description: 'Personalized landing pages for leads with enriched data and product recommendations',
    businessContext: 'Sales teams create and manage custom experiences for prospects with automatic data enrichment, product matching, and personalized greetings based on incoming business metrics.',
    children: [
      { id: 'personal-cards-list', label: 'Manage Cards', path: '/personal-cards', icon: Contact, description: 'View and manage all personal cards', businessContext: 'Manage personalized landing pages with status tracking and link sharing.' }
    ]
  },
  {
    id: 'collaborations',
    label: 'Collaborations',
    icon: UsersRound,
    description: 'Manage collaboration partnerships, campaigns, and outreach',
    businessContext: 'Partnership managers and business development teams coordinate collaborations with external partners, influencers, and industry leaders through structured outreach campaigns.',
    children: [
      { 
        id: 'collaboration-types', 
        label: 'Collaborations', 
        path: '/collaboration-types', 
        icon: Tag,
        description: 'Define and manage different collaboration partnership types',
        businessContext: 'Partnership directors configure collaboration types like webinars, podcasts, and LinkedIn Top Voice programs to categorize and track different partnership opportunities.'
      },
      { 
        id: 'collaborators', 
        label: 'Collaborators', 
        path: '/collaborators', 
        icon: UsersRound,
        description: 'Manage collaboration partners and leads database',
        businessContext: 'Business development teams maintain a database of potential and active collaboration partners, tracking contact information, collaboration type, and partnership status from initial outreach through completion.'
      },
      { 
        id: 'collaboration-campaigns', 
        label: 'Collaboration Campaigns', 
        path: '/collaboration-campaigns', 
        icon: Mail,
        description: 'Create and manage email outreach campaigns for collaborations',
        businessContext: 'Partnership coordinators design and execute multi-touch email campaigns to recruit collaborators, nurture relationships, and convert leads into active partnerships through automated sequences.'
      },
      { 
        id: 'collaborations-email-analytics', 
        label: 'Email Analytics', 
        path: '/collaborations/email-analytics', 
        icon: Mail,
        description: 'Collaborations email queue and engagement',
        businessContext: 'Monitor os_email_queue: queue, delivery, opens, clicks for collaboration outreach.'
      }
    ]
  },
  {
    id: 'growth-hacking',
    label: 'Growth Hacking',
    icon: growthHackingIcon,
    description: 'Rapid experimentation framework for growth optimization',
    businessContext: 'Growth teams run structured experiments to find scalable growth levers through hypothesis testing and data-driven iteration.',
    children: [
      { id: 'growthhacking-about', label: 'About Growth Hacking', path: '/growthhacking/about', icon: BookOpen, description: 'Growth hacking methodology guide', businessContext: 'Teams learn the framework and best practices.' },
      { id: 'gh-onepager', label: 'Growth Modeling', path: '/gh-onepager', icon: Columns3, description: 'Growth model and key metrics snapshot', businessContext: 'Executives review growth strategy at a glance.' },
      { id: 'growth-metrics-cockpit', label: 'Metrics Cockpit', path: '/growth-metrics-cockpit', icon: LayoutDashboard, description: 'Growth metrics dashboard with monthly analysis', businessContext: 'Monitor North Star and Supportive metrics across all pillars with current month, last month, and year-to-date trends.' },
      { id: 'gh-dashboard', label: 'Dashboard', path: '/gh-dashboard', icon: Lightbulb, description: 'Growth experiment performance overview', businessContext: 'Growth leads monitor active experiments and wins.' },
      { id: 'brainstorming-ideas', label: 'Brainstorming Ideas', path: '/brainstorming-ideas', icon: Lightbulb, description: 'Generate and capture growth experiment ideas', businessContext: 'Teams ideate potential growth levers to test.' },
      { id: 'prioritization', label: 'Prioritization', path: '/prioritization', icon: Target, description: 'Score and rank experiments by ICE framework', businessContext: 'Growth managers prioritize tests by impact, confidence, ease.' },
      { id: 'sprints', label: 'Sprints', path: '/sprints', icon: CalendarDays, description: 'Growth experiment sprint planning', businessContext: 'Teams batch experiments into 2-week testing cycles.' },
      { id: 'growth-planning', label: 'Growth Planning', path: '/growth-planning', icon: Kanban, description: 'Long-term growth roadmap', businessContext: 'Strategists plan quarters ahead with hypothesis pipeline.' },
      { id: 'growth-tasks', label: 'Growth Tasks', path: '/growth-tasks', icon: CheckSquare, description: 'Experiment implementation task tracking', businessContext: 'Engineers and designers execute experiment builds.' },
      { id: 'growth-tasking', label: 'Growth Tasking', path: '/growth-tasking', icon: ClipboardList, description: 'Task assignment and delegation', businessContext: 'Leads distribute experiment work across team.' },
      { id: 'growth-learnings', label: 'Growth Learnings', path: '/growth-learnings', icon: BookOpen, description: 'Experiment results and knowledge base', businessContext: 'Teams document what worked, what failed, and why.' },
      { id: 'sales-channels', label: 'Sales Channels', path: '/sales-channels', icon: ShoppingCart, description: 'Manage sales channels for growth experiments', businessContext: 'Teams organize growth initiatives by sales channel to track performance and optimize conversion across different customer acquisition paths.' },
      { id: 'gh-performance-insights', label: 'Performance Insights', path: '/gh-performance-insights', icon: BarChart3, description: 'Hierarchical view of sprints, ideas, and tasks with phase tracking', businessContext: 'Growth leads analyze performance across sprints, ideas, and tasks to identify bottlenecks and track execution velocity.' }
    ]
  },
  {
    id: 'link-building',
    label: 'Link Building',
    icon: Rabbit,
    description: 'Backlink acquisition and partnership management',
    businessContext: 'SEO link builders manage relationships with websites, track link exchanges, and build domain authority through strategic partnerships.',
    children: [
      { id: 'link-building-exchanges', label: 'My Exchanges', path: '/link-building/exchanges', icon: LinkIcon, description: 'Manage reciprocal link agreements', businessContext: 'Teams track give-and-get link partnerships.' },
      { id: 'link-building-analytics', label: 'Analytics', path: '/link-building/analytics', icon: BarChart3, description: 'Backlink profile growth and metrics', businessContext: 'SEOs track acquired links and domain authority gains.' },
      { id: 'link-building-partners', label: 'Partners', path: '/link-building/partners', icon: Link2, description: 'Website partnership database', businessContext: 'Outreach teams manage relationships with linking domains.' },
      { id: 'link-building-domains', label: 'Domains', path: '/link-building/domains', icon: Globe, description: 'Target domain list for outreach', businessContext: 'Link builders identify high-authority sites to pitch.' },
      { id: 'link-building-email-history', label: 'Email History', path: '/link-building/email-history', icon: Mail, description: 'Outreach email tracking and follow-ups', businessContext: 'Specialists monitor pitch responses and conversions.' }
    ]
  },
  {
    id: 'cold-outreach',
    label: 'Cold Outreach',
    icon: Send,
    description: 'Email outreach campaigns for lead generation',
    businessContext: 'Sales development reps run cold email campaigns to generate leads, book meetings, and build pipeline.',
    children: [
      { id: 'cold-outreach-campaigns', label: 'Campaigns', path: '/cold-outreach/campaigns', icon: Send, description: 'Manage outbound email campaigns', businessContext: 'SDRs create and launch multi-touch email sequences.' },
      { id: 'cold-outreach-lists', label: 'Contact Lists', path: '/cold-outreach/lists', icon: Users, description: 'Organize prospect contact lists', businessContext: 'Ops teams segment prospects by industry, title, company size.' },
      { id: 'cold-outreach-contacts', label: 'Contacts', path: '/cold-outreach/contacts', icon: Users, description: 'Individual prospect records', businessContext: 'Reps view contact details and engagement history.' },
      { id: 'cold-outreach-dispatch', label: 'Dispatch Center', path: '/cold-outreach/dispatch', icon: Send, description: 'Send and schedule outreach emails', businessContext: 'SDRs launch campaigns and manage send timing.' },
      { id: 'cold-outreach-analytics', label: 'Analytics', path: '/cold-outreach/analytics', icon: BarChart3, description: 'Outreach performance metrics', businessContext: 'Managers track open rates, reply rates, and conversions.' },
      { id: 'cold-outreach-email-analytics', label: 'Email Analytics', path: '/cold-outreach/email-analytics', icon: Mail, description: 'Cold outreach email queue and engagement', businessContext: 'Monitor os_email_queue: queue, delivery, opens, clicks.' }
    ]
  },
  {
    id: 'referral-system',
    label: 'Referral System',
    icon: Share2,
    description: 'Referral codes, programs, waitlists, and email flows',
    businessContext: 'Teams run referral and waitlist programs; contacts get shareable codes (e.g. /join?ref=CODE); email flows support AI-generated sequences (automatic = run on events, manual = initiate via Dispatch Center).',
    children: [
      { id: 'referral-contacts', label: 'Contacts', path: '/referral/contacts', icon: Users, description: 'Referral contacts and their codes', businessContext: 'View and manage people in the referral module; copy share link /join?ref=CODE.' },
      { id: 'referral-programs', label: 'Programs', path: '/referral/programs', icon: Target, description: 'Milestone, waiting list, and generic programs', businessContext: 'Create programs and add members; track waitlist position.' },
      { id: 'referral-lists', label: 'Lists', path: '/referral/lists', icon: ListChecks, description: 'Contact lists for dispatch; add by criteria (e.g. converted)', businessContext: 'Create lists and add members by program and status; use lists in Dispatch Center to run manual email flows.' },
      { id: 'referral-campaigns', label: 'Email Flows', path: '/referral/campaigns', icon: Send, description: 'Referral email flows with AI-generated sequences', businessContext: 'Build sequences (Welcome, Re-engagement, Milestone); automatic = run on events, manual = initiate via Dispatch; A/B variants supported.' },
      { id: 'referral-dispatch', label: 'Dispatch Center', path: '/referral/dispatch', icon: Send, description: 'Manually initiate manual email flows to lists', businessContext: 'Start manual flows for a list; automatic flows run on their own.' },
      { id: 'referral-prompts', label: 'Prompts', path: '/referral/prompts', icon: Wand2, description: 'Prompts for AI email sequence generation', businessContext: 'Manage prompts used when generating email flow sequences.' },
      { id: 'referral-analytics', label: 'Analytics', path: '/referral/analytics', icon: BarChart3, description: 'Referral and email flow metrics', businessContext: 'Track contacts, program sizes, and email flow performance.' },
      { id: 'referral-email-analytics', label: 'Email Analytics', path: '/referral/email-analytics', icon: Mail, description: 'Referral email sends and engagement', businessContext: 'Monitor os_referral_sends: queue, delivery, opens, replies, unsubscribes.' },
      {
        id: 'referral-public-path',
        label: 'Public Path',
        icon: Store,
        description: 'Customer-facing referral pages',
        businessContext: 'Participants use the Referral Control Center to view their programs, share links, and see leaderboards. Access is secured with email PIN authentication.',
        children: [
          {
            id: 'referral-control-center',
            label: 'Referral Control Center',
            path: '/referral-control-center',
            icon: LayoutDashboard,
            description: 'PIN-protected dashboard for referral participants',
            businessContext: 'Participants sign in with email OTP to view their programs, copy share links, see leaderboards, and who referred whom.'
          }
        ]
      }
    ]
  },
  {
    id: 'tool-site-audit',
    label: 'Tool: Site audit',
    icon: ScanFace,
    description: 'Website SEO audit and analysis tool',
    businessContext: 'SEO specialists and marketing teams audit websites to identify optimization opportunities, track performance metrics, and generate comprehensive SEO reports for clients.',
    children: [
      { 
        id: 'siteaudit-dashboard', 
        label: 'Dashboard', 
        path: '/siteaudit/dashboard', 
        icon: LayoutDashboard, 
        description: 'Complete SEO analysis dashboard with module summaries', 
        businessContext: 'SEO managers view overall website health scores and module performance at a glance.' 
      },
      { 
        id: 'siteaudit-operations', 
        label: 'Operations', 
        path: '/siteaudit/operations', 
        icon: Cog, 
        description: 'Manage your websites and monitor their SEO analysis status', 
        businessContext: 'Agency teams manage multiple client websites, track scan status, and organize audit workflows.' 
      },
      { 
        id: 'siteaudit-main-elements', 
        label: 'Main Elements', 
        path: '/siteaudit/main-elements', 
        icon: Globe, 
        description: 'Essential SEO elements analysis including titles, meta descriptions, and headings', 
        businessContext: 'SEO specialists review and optimize core on-page elements that impact search rankings.' 
      },
      { 
        id: 'siteaudit-open-graph', 
        label: 'Open Graph Metadata', 
        path: '/siteaudit/open-graph', 
        icon: ImageIcon, 
        description: 'Social media sharing optimization', 
        businessContext: 'Social media managers ensure proper Open Graph tags for rich previews when content is shared.' 
      },
      { 
        id: 'siteaudit-pixels-tags', 
        label: 'Pixels & Tags', 
        path: '/siteaudit/pixels-tags', 
        icon: Target, 
        description: 'Analytics and tracking setup', 
        businessContext: 'Marketing ops teams verify tracking pixels and analytics tags are properly implemented.' 
      },
      { 
        id: 'siteaudit-marktech', 
        label: 'Marktech Analysis', 
        path: '/siteaudit/marktech', 
        icon: Network, 
        description: 'Marketing technology stack', 
        businessContext: 'Tech stack analysts identify which marketing tools and platforms are integrated on the website.' 
      },
      { 
        id: 'siteaudit-speed-performance', 
        label: 'Speed Performance', 
        path: '/siteaudit/speed-performance', 
        icon: Zap, 
        description: 'Loading speed and performance', 
        businessContext: 'Performance engineers analyze page load times and Core Web Vitals to optimize user experience.' 
      },
      { 
        id: 'siteaudit-mobile-analysis', 
        label: 'Mobile Analysis', 
        path: '/siteaudit/mobile-analysis', 
        icon: Smartphone, 
        description: 'Mobile optimization and usability', 
        businessContext: 'Mobile specialists ensure websites are responsive and optimized for mobile devices.' 
      },
      { 
        id: 'siteaudit-expert-validation', 
        label: 'Expert Validation', 
        path: '/siteaudit/expert-validation', 
        icon: UserCheck, 
        description: 'Review and validate automated scores for all modules', 
        businessContext: 'SEO experts review automated analysis results, adjust scores, and provide expert reasoning for client reports.' 
      },
      { 
        id: 'siteaudit-webhook-settings', 
        label: 'Webhook Settings', 
        path: '/siteaudit/webhook-settings', 
        icon: LinkIcon, 
        description: 'Configure webhook URLs for notifications', 
        businessContext: 'DevOps teams set up webhooks to receive real-time notifications when scans complete.' 
      }
    ]
  },
  {
    id: 'documentation-hub',
    label: 'Documentation Hub',
    icon: BookOpen,
    description: 'Centralized knowledge base for team resources',
    businessContext: 'Teams organize event photos, documents, tips, code snippets, and meeting transcripts for easy reference and knowledge sharing.',
    children: [
      { id: 'doc-hub-dashboard', label: 'Dashboard', path: '/documentation-hub/dashboard', icon: LayoutDashboard, description: 'Documentation hub overview', businessContext: 'Teams navigate to different resource types.' },
      { id: 'doc-hub-events', label: 'Photos/Videos', path: '/documentation-hub/events', icon: Calendar, description: 'Event photos and video archives', businessContext: 'Marketing captures event content for reuse.' },
      { id: 'doc-hub-documents', label: 'Documents', path: '/documentation-hub/documents', icon: FileText, description: 'Shared document library', businessContext: 'Teams access templates, decks, and resources.' },
      { id: 'doc-hub-tips', label: 'Tips', path: '/documentation-hub/tips', icon: Lightbulb, description: 'Best practices and how-to guides', businessContext: 'Experts share tips and tribal knowledge.' },
      { id: 'doc-hub-snippets', label: 'Snippets', path: '/documentation-hub/snippets', icon: Code, description: 'Reusable code and text templates', businessContext: 'Developers save code blocks and canned responses.' },
      { id: 'project-moms', label: 'MoMs & Transcripts', path: '/documentation-hub/project-moms', icon: FileText, description: 'Meeting minutes and transcriptions', businessContext: 'Teams document decisions and action items.' }
    ]
  },
  {
    id: 'embeddable-widgets',
    label: 'Embeddable Widgets',
    icon: Plug,
    description: 'Manage embeddable widgets for your website',
    businessContext: 'Marketing teams configure and embed interactive widgets like chat assistants, voice bots, and lead capture forms on their website to engage visitors and collect leads.',
    children: [
      { 
        id: 'public-chats', 
        label: 'My Widgets', 
        path: '/public-chats',
        icon: MessageSquare,
        description: 'Manage multiple chat widgets for your brand',
        businessContext: 'Marketing managers create and configure multiple chat widgets with different purposes, such as support chat, sales chat, or product-specific assistants. Each chat can have its own configuration, knowledge base, and appearance.'
      },
      { 
        id: 'public-chat-widget-config', 
        label: "Configuration", 
        path: '/public-chat-widget-config',
        icon: Settings,
        description: 'Configure public-facing AI chat widget for your website',
        businessContext: 'Website managers customize the chat assistant\'s appearance, behavior, and AI model to provide visitor support and lead qualification.'
      },
    { 
      id: 'public-chat-knowledge', 
      label: "Widget's Brain Config", 
      path: '/public-chat-knowledge', 
      icon: Brain,
      description: 'Control what information your public chat widget can access and share',
      businessContext: 'Brand managers define which data sources (products, pricing, blog content, team info) the public chat widget can use when responding to visitor questions. Includes calendar booking configuration for meeting scheduling.'
    }
    ]
  },
  {
    id: 'scale-2y3x',
    label: 'Scale at Speed: 2Y3X',
    icon: twoY3XIcon,
    description: 'Strategic framework to triple revenue in two years',
    businessContext: 'Executive teams execute the 2Y3X methodology for rapid, sustainable growth through systematic strategy and execution.',
    children: [
      { id: 'scale-2y3x-about', label: 'About 2Y3X', path: '/scale-2y3x/about', icon: about2y3xIcon, description: '2Y3X methodology explanation', businessContext: 'Leaders learn the framework principles.' },
      { id: 'scale-2y3x-dashboard', label: 'Dashboard', path: '/scale-2y3x/dashboard', icon: Rocket, description: '2Y3X progress tracking', businessContext: 'Executives monitor progress toward 3X goal.' },
      { id: 'scale-2y3x-builder', label: 'Strategy Map', path: '/scale-2y3x/2y3x-builder', icon: Wand2, description: 'Build visual strategy roadmap', businessContext: 'Leadership maps growth strategy components.' },
      { id: 'scale-2y3x-goals', label: '2Y3X Goals', path: '/scale-2y3x/goals', icon: Target, description: 'Define and track strategic goals', businessContext: 'Teams set OKRs aligned with 3X vision.' },
      { id: 'scale-2y3x-growth-lab-team', label: 'Growth Lab Team', path: '/scale-2y3x/growth-lab-team', icon: Users, description: 'Core growth team members', businessContext: 'Leaders assemble dedicated growth squad.' },
      { id: 'scale-2y3x-ideas', label: 'Initiatives', path: '/scale-2y3x/ideas', icon: Lightbulb, description: 'Growth initiative pipeline', businessContext: 'Teams capture and evaluate growth opportunities.' },
      { id: 'scale-2y3x-current-quarter-tasks', label: 'Current Quarter Tasks', path: '/scale-2y3x/current-quarter-tasks', icon: Kanban, description: 'Initiative task breakdown', businessContext: 'Leads plan initiative execution steps.' },
      { id: 'scale-2y3x-tasks', label: 'Tasks', path: '/scale-2y3x/tasks', icon: CheckSquare, description: 'All 2Y3X tasks with filters', businessContext: 'Teams view and filter all 2Y3X tasks.' },
      { id: 'scale-2y3x-quarterly-tasks', label: 'Quarterly Tasks', path: '/scale-2y3x/quarterly-tasks', icon: CheckSquare, description: 'Kanban board by sprint', businessContext: 'Teams execute and monitor initiative progress.' },
      { id: 'scale-2y3x-performance-insights', label: 'Task Progress Dashboard', path: '/scale-2y3x/performance-insights', icon: BarChart3, description: 'Growth performance analytics', businessContext: 'Leaders analyze what drives growth velocity.' }
    ]
  },
  {
    id: 'local-stores',
    label: 'Local Stores / Businesses',
    icon: Store,
    description: 'Retail location performance tracking and optimization',
    businessContext: 'Multi-location businesses analyze individual store performance to identify top performers and opportunities for improvement.',
    children: [
      { id: 'retail-data', label: 'Retail Store Data', path: '/retail-data', icon: Store, description: 'Store-level performance dashboard', businessContext: 'Retail managers compare locations and identify trends.' },
      { id: 'retail-store-data-loading', label: 'Data Loading', path: '/retail-store-data-loading', icon: Upload, description: 'Import store sales and traffic data', businessContext: 'Ops teams upload POS and foot traffic data.' },
      { id: 'retail-store-performance', label: 'Performance Analysis', path: '/retail-store-performance', icon: TrendingUp, description: 'Store ranking and benchmarking', businessContext: 'District managers identify top and underperforming stores.' },
      { id: 'retail-store-micro-performance', label: 'Micro-Performance Analysis', path: '/retail-store-micro-performance', icon: BarChart3, description: 'Granular store metrics deep-dive', businessContext: 'Analysts examine product mix, staffing, and local factors.' },
      { id: 'retail-store-settings', label: 'Settings', path: '/retail-store-settings', icon: Settings, description: 'Configure store data sources', businessContext: 'Admins manage store list and data integrations.' }
    ]
  },
  {
    id: 'learning-center',
    label: 'Learning Center',
    icon: GraduationCap,
    description: 'Comprehensive LMS for creating courses, managing student enrollments, and tracking learning progress',
    businessContext: 'Training managers and educators create structured learning experiences with modules, lessons, quizzes, and certificates. Track student engagement, measure course effectiveness, and provide scalable training programs.',
    children: [
      {
        id: 'lms-dashboard',
        label: 'LMS Dashboard',
        path: '/lms/dashboard',
        icon: LayoutDashboard,
        description: 'Overview of all courses, enrollments, and learning metrics',
        businessContext: 'Main dashboard for LMS administrators to view course stats, recent enrollments, and overall learning platform performance.'
      },
      {
        id: 'lms-coursemanagement',
        label: 'Course Management',
        path: '/lms/coursemanagement',
        icon: BookOpen,
        description: 'Create and manage learning courses, modules, and lessons',
        businessContext: 'Course administrators build structured learning paths, organize content into modules, drag-drop reorder lessons, and manage course publication status with pricing.'
      },
      {
        id: 'lms-instructors',
        label: 'Instructors',
        path: '/lms/instructors',
        icon: UserSquare,
        description: 'Manage course instructors and their profiles',
        businessContext: 'Course administrators create and manage instructor profiles with bios, social links, and images. Assign instructors to courses to provide clear attribution and build instructor credibility.'
      },
      {
        id: 'lms-course-types',
        label: 'Course Types',
        path: '/lms/course-types',
        icon: BookOpen,
        description: 'Define and manage course types',
        businessContext: 'Categorize courses into different types for better organization and filtering.'
      },
      {
        id: 'lms-enrollments',
        label: 'Student Enrollments',
        path: '/lms/enrollments',
        icon: Users,
        description: 'Manage student enrollments, track progress, and bulk import students',
        businessContext: 'Enrollment managers add students individually or via bulk CSV import, monitor their course progress, and issue certificates upon completion.'
      },
      {
        id: 'lms-company-enrollments',
        label: 'Company Enrollments',
        path: '/lms/company-enrollments',
        icon: UsersRound,
        description: 'Manage company enrollments and bulk course access for organizations',
        businessContext: 'Enterprise training managers create company-level enrollments to provide course access to multiple employees within organizations. Track company-wide learning metrics and manage bulk enrollment status.'
      },
      {
        id: 'lms-analytics',
        label: 'Learning Analytics',
        path: '/lms/analytics',
        icon: BarChart3,
        description: 'Track student enrollments, course completion rates, and learning progress',
        businessContext: 'Training managers analyze student engagement metrics, identify struggling learners, measure course effectiveness, and optimize learning content based on performance data.'
      },
      {
        id: 'lms-feedback',
        label: 'Feedback',
        path: '/lms/feedback',
        icon: Heart,
        description: 'View lesson claps and student feedback across courses',
        businessContext: 'Training administrators monitor student engagement through lesson claps, track popular content, and identify high-performing lessons based on student reactions.'
      },
      {
        id: 'lms-ask-for-more',
        label: 'Ask For More',
        path: '/lms/ask-for-more',
        icon: MessageSquare,
        description: 'Manage student requests for additional content and topics',
        businessContext: 'Course administrators review and process student requests for more content on specific topics. Track request status (asked, confirmed, planned, rejected) and provide feedback to students.'
      },
      {
        id: 'lms-email-analytics',
        label: 'Email Analytics',
        path: '/lms/email-analytics',
        icon: Mail,
        description: 'Monitor LMS email queue, delivery status, and engagement metrics',
        businessContext: 'Training administrators monitor email delivery, track open and click rates, manage email queue, and troubleshoot delivery issues for course-related communications.'
      },
      {
        id: 'lms-quiz-management',
        label: 'Quiz Management',
        path: '/lms/quiz-management',
        icon: CheckSquare,
        description: 'Create and manage quizzes, questions, and assessments for courses',
        businessContext: 'Course administrators build quizzes with multiple question types, set passing scores, and track student assessment results to measure learning comprehension.'
      },
      {
        id: 'lms-quiz-submissions',
        label: 'Quiz Submissions',
        path: '/lms/quiz-submissions',
        icon: ClipboardList,
        description: 'Review student quiz attempts and results',
        businessContext: 'Training administrators review individual student quiz submissions, analyze answer patterns, and identify knowledge gaps across the learning cohort.'
      },
      {
        id: 'lms-certificate-templates',
        label: 'Certificate Templates',
        path: '/lms/certificate-templates',
        icon: FileText,
        description: 'Design and manage certificate templates for course completion',
        businessContext: 'Training administrators create customizable certificate templates with brand elements that are automatically generated upon student course completion.'
      },
      {
        id: 'lms-student-eligibility',
        label: 'Student Eligibility',
        path: '/lms/student-eligibility',
        icon: UserCheck,
        description: 'Manage company domains eligible for student enrollment',
        businessContext: 'Administrators maintain whitelist of approved company domains and email addresses that are authorized to enroll in courses, ensuring access control for enterprise training programs.'
      },
      {
        id: 'lms-associations',
        label: 'Associations',
        path: '/lms/associations',
        icon: Network,
        description: 'Manage student associations and affiliations',
        businessContext: 'Administrators create and manage associations that students can be affiliated with, such as professional organizations, universities, or partner companies. These associations can be referenced when granting student eligibility.'
      },
      {
        id: 'lms-academies',
        label: 'Academies',
        path: '/lms/academies',
        icon: GraduationCap,
        description: 'Manage academies to organize courses, instructors, and associations',
        businessContext: 'Administrators create and configure academies as organizational units for grouping related courses, instructors, and associations. Academies provide branding and structure for the public learning catalog while remaining purely organizational metadata.'
      },
      {
        id: 'lms-shares',
        label: 'Share with people & groups',
        path: '/learning-center-shares',
        icon: UserPlus,
        description: 'Share learning center progress reports with stakeholders',
        businessContext: 'Training managers schedule and send progress reports to stakeholders for academies and courses. Track enrollments, completions, and learning metrics in automated scheduled reports.'
      },
      {
        id: 'lms-settlements',
        label: 'Settlements',
        path: '/lms/settlements',
        icon: Banknote,
        description: 'Manage instructor and association payment settlements',
        businessContext: 'Finance administrators create and manage payment settlements for instructors and associations. Track pending, approved, and paid settlements with proforma invoice generation.'
      },
      {
        id: 'lms-leads',
        label: 'Leads',
        path: '/lms/leads',
        icon: UserPlus,
        description: 'Manage marketing leads captured from course landing pages',
        businessContext: 'Marketing teams view and manage leads captured through LMS course forms, tracking contact information, lead types, and course associations for follow-up and enrollment conversion.'
      },
      {
        id: 'student-path',
        label: 'Student Path',
        icon: ShieldCheck,
        description: 'OTP-protected student portal for accessing courses and certificates',
        businessContext: 'Students use email OTP authentication to access their enrolled courses, view lessons, track progress, and download certificates. All routes are secured with one-time password verification.',
        children: [
          {
            id: 'learn-auth',
            label: 'Student Login (Membership)',
            path: '/learn/auth',
            icon: ShieldCheck,
            description: 'Email OTP authentication for company/membership enrollments',
            businessContext: 'Students from company enrollments enter email and receive OTP code to access courses their organization has enrolled them in.'
          },
          {
            id: 'learn-login',
            label: 'Student Login (Course)',
            path: '/learn/login',
            icon: ShieldCheck,
            description: 'Email OTP authentication for individual course purchases',
            businessContext: 'Students who purchased individual courses enter email and receive OTP code to access their purchased courses.'
          },
          {
            id: 'learn-dashboard',
            label: 'Course List',
            path: '/learn/dashboard',
            icon: LayoutDashboard,
            description: 'Student view of enrolled courses and learning progress',
            businessContext: 'Students see their active courses, track completion progress, and access lessons.'
          },
          {
            id: 'learn-verify-certificate',
            label: 'Verify Certificate',
            path: '/learn/verify-certificate',
            icon: CheckSquare,
            description: 'Public certificate verification by code',
            businessContext: 'Anyone can verify certificate authenticity by entering the verification code from a certificate.'
          },
        ]
      },
      {
        id: 'public-path',
        label: 'Public Path',
        icon: Store,
        description: 'Public course marketplace for browsing and purchasing courses',
        businessContext: 'Public users can browse available courses, view details, and purchase access. This is the customer-facing storefront for the LMS, accessible without authentication.',
        children: [
          {
            id: 'public-academies',
            label: 'Academy Catalog',
            path: '/academies',
            icon: GraduationCap,
            description: 'Browse all published academies',
            businessContext: 'Visitors can explore academies that organize courses, instructors, and associations into branded learning programs.'
          },
          {
            id: 'public-academy-detail',
            label: 'Academy Details',
            path: '/academies/example-academy',
            icon: GraduationCap,
            description: 'View academy information and available courses',
            businessContext: 'Prospective students can review academy details including description, logo, and browse courses offered by the academy.'
          },
          {
            id: 'public-academy-courses',
            label: 'Academy Courses',
            path: '/academies/example-academy/courses',
            icon: BookOpen,
            description: 'Browse courses filtered by academy',
            businessContext: 'Visitors can explore all courses belonging to a specific academy, with the same filtering and browsing capabilities as the main course catalog.'
          },
          {
            id: 'public-courses',
            label: 'Course Catalog',
            path: '/courses',
            icon: Store,
            description: 'Browse and search all published courses',
            businessContext: 'Visitors can explore the full course catalog, filter by category/difficulty/price, and view course cards with ratings and enrollment counts.'
          },
          {
            id: 'public-course-detail',
            label: 'Course Details',
            path: '/courses/example-course',
            icon: BookOpen,
            description: 'View course curriculum, reviews, and purchase options',
            businessContext: 'Prospective students can review full course details including modules, lessons, instructor info, reviews, and pricing before purchasing.'
          },
          {
            id: 'course-checkout',
            label: 'Checkout',
            path: '/courses/checkout/example-course',
            icon: ShoppingCart,
            description: 'Complete course purchase with Stripe',
            businessContext: 'Students provide contact info and payment details to enroll in a paid course via Stripe checkout.'
          },
          {
            id: 'payment-success',
            label: 'Payment Success',
            path: '/courses/payment/success',
            icon: CheckSquare,
            description: 'Course purchase confirmation and next steps',
            businessContext: 'After successful payment, students receive confirmation and instructions to access their course via OTP email.'
          },
          {
            id: 'payment-cancel',
            label: 'Payment Cancelled',
            path: '/courses/payment/cancel',
            icon: AlertTriangle,
            description: 'Payment cancellation page',
            businessContext: 'If a student cancels during checkout, they can return to the course or browse other courses.'
          }
        ]
      }
    ]
  },
  {
    id: 'human-resources',
    label: 'Human Resources',
    icon: Users,
    description: 'Manage employees and HR operations',
    businessContext: 'HR managers maintain employee records, manage organizational structure, and handle employee lifecycle.',
    children: [
      {
        id: 'hr-employees',
        label: 'Employees',
        path: '/human-resources/employees',
        icon: Users,
        description: 'Manage employee records',
        businessContext: 'Maintain comprehensive employee data including personal info, employment details, and organizational hierarchy.'
      },
      {
        id: 'hr-noteworthy-events',
        label: 'Noteworthy Events',
        path: '/human-resources/noteworthy-events',
        icon: Calendar,
        description: 'Track important employee events',
        businessContext: 'Record and track significant events for employees such as promotions, awards, achievements, and milestones.'
      },
      {
        id: 'hr-positions',
        label: 'Positions',
        path: '/human-resources/positions',
        icon: Briefcase,
        description: 'Define job positions for the organization',
        businessContext: 'Manage job positions that can be linked to evaluation criteria and employee records.'
      },
      {
        id: 'hr-org-chart',
        label: 'Organization Chart',
        path: '/human-resources/org-chart',
        icon: Network,
        description: 'Visualize organizational hierarchy',
        businessContext: 'View the reporting structure and employee hierarchy as an interactive organization chart.'
      },
      {
        id: 'hr-evaluation-categories',
        label: 'Evaluation Categories',
        path: '/internal-training/evaluation-categories',
        icon: FolderOpen,
        description: 'Manage Hard Skills/Soft Skills categories',
        businessContext: 'Define evaluation category groups for organizing assessment criteria.'
      },
      {
        id: 'hr-evaluation-criteria',
        label: 'Evaluation Criteria',
        path: '/internal-training/evaluation-criteria',
        icon: ListChecks,
        description: 'Define criteria with codes and weights',
        businessContext: 'Configure the specific criteria used to evaluate employee performance with weighted scoring.'
      },
      {
        id: 'hr-evaluations',
        label: 'Employee Evaluations',
        path: '/internal-training/evaluations',
        icon: ClipboardCheck,
        description: 'Conduct employee evaluations with scoring',
        businessContext: 'Perform periodic employee evaluations using defined criteria and track performance over time.'
      },
      {
        id: 'hr-vacations',
        label: 'Vacation Management',
        path: '/human-resources/vacations',
        icon: Calendar,
        description: 'Manage employee vacation requests and approvals',
        businessContext: 'Employees request vacations, managers approve with task visibility, and HR tracks vacation balances and usage across the organization.'
      },
      {
        id: 'hr-overtime',
        label: 'Overtime Management',
        path: '/human-resources/overtime',
        icon: Clock,
        description: 'Manage employee overtime requests and approvals',
        businessContext: 'Employees request overtime hours, supervisors approve, and track usage.'
      },
      {
        id: 'hr-job-posts',
        label: 'Job Posts',
        path: '/human-resources/job-posts',
        icon: FileText,
        description: 'Manage job postings for recruitment',
        businessContext: 'Create and manage job listings with detailed descriptions, requirements, and benefits to attract qualified candidates.'
      },
      {
        id: 'hr-applicants',
        label: 'Applicants',
        path: '/human-resources/applicants',
        icon: UserPlus,
        description: 'Track and manage job applicants',
        businessContext: 'Review applications, track candidate progress through hiring pipeline, and use AI-powered analysis for fit scoring and interview prep.'
      }
    ]
  },
  {
    id: 'internal-training',
    label: 'Internal Training',
    icon: GraduationCap,
    description: 'Train your internal team with LMS lessons and track progress',
    businessContext: 'Training managers assign LMS lessons to growth squad members, track video views and quiz completion, and run triggered email campaigns based on learner behavior.',
    children: [
      {
        id: 'internal-training-dashboard',
        label: 'Dashboard',
        path: '/internal-training/dashboard',
        icon: LayoutDashboard,
        description: 'Overview of internal training metrics and progress',
        businessContext: 'View aggregate statistics on trainee progress, completion rates, and overdue assignments.'
      },
      {
        id: 'internal-training-programs',
        label: 'Programs',
        path: '/internal-training/programs',
        icon: BookOpen,
        description: 'Create and manage training programs',
        businessContext: 'Organize lessons into structured programs with descriptions and schedules.'
      },
      {
        id: 'internal-training-assignments',
        label: 'Assignments',
        path: '/internal-training/assignments',
        icon: Users,
        description: 'Assign lessons to team members',
        businessContext: 'Assign specific lessons to squad members with due dates and track their progress.'
      },
      {
        id: 'internal-training-progress',
        label: 'Progress Tracking',
        path: '/internal-training/progress',
        icon: BarChart3,
        description: 'Track trainee progress and completion rates',
        businessContext: 'Monitor which trainees have watched videos, completed quizzes, and need reminders.'
      },
      {
        id: 'internal-training-needs',
        label: 'Needs Assessment',
        path: '/internal-training/needs',
        icon: Target,
        description: 'Identify and track skill gaps across your team',
        businessContext: 'Document training needs, prioritize skill gaps, and link to recommended lessons.'
      },
      {
        id: 'internal-training-shares',
        label: 'Share with people & groups',
        path: '/internal-training-shares',
        icon: UserPlus,
        description: 'Share training progress reports with stakeholders',
        businessContext: 'Configure automated report sharing with external stakeholders.'
      }
    ]
  },
  {
    id: 'surveys',
    label: 'Surveys',
    icon: ClipboardList,
    description: 'Create and manage market research surveys with AI processing',
    businessContext: 'Market researchers create surveys, send invitations to internal or external recipients, collect responses, process them with AI for insights, and generate Gamma presentations to present results.',
    children: [
      {
        id: 'surveys-list',
        label: 'All Surveys',
        path: '/surveys',
        icon: ClipboardList,
        description: 'View and manage all surveys',
        businessContext: 'Survey managers view all surveys, filter by status, and access survey details, results, and settings.'
      },
      {
        id: 'surveys-create',
        label: 'Create Survey',
        path: '/surveys/new',
        icon: Plus,
        description: 'Create a new market research survey',
        businessContext: 'Create new surveys with questions, set due dates, configure AI processing prompts, and define recipient types.'
      },
      {
        id: 'surveys-recipients',
        label: 'Manage Recipients',
        path: '/surveys/recipients',
        icon: Users,
        description: 'Upload CSV or manually add survey recipients',
        businessContext: 'Add recipients via CSV upload or manual entry, mark as internal or external, and send invitation emails with unique survey links. Edit recipients and copy from other surveys.'
      },
      {
        id: 'surveys-submissions',
        label: 'View Submissions',
        path: '/surveys/submissions',
        icon: FileText,
        description: 'View all survey responses with statistics',
        businessContext: 'View individual submissions, completion rates, response statistics, and detailed answers from all survey respondents across all surveys.'
      },
      {
        id: 'surveys-process',
        label: 'Process Submissions',
        path: '/surveys/process',
        icon: Sparkles,
        description: 'Process survey responses with AI analysis',
        businessContext: 'Aggregate survey responses, analyze with AI using custom prompts, generate insights and recommendations, and prepare data for Gamma presentations across all surveys.'
      },
      {
        id: 'surveys-results',
        label: 'View Results',
        path: '/surveys/results',
        icon: BarChart3,
        description: 'View survey analytics and AI insights',
        businessContext: 'Access comprehensive survey results including response breakdowns by question, AI-generated insights, recommendations, and Gamma presentation links across all surveys.'
      },
      {
        id: 'surveys-email-tracking',
        label: 'Email Tracking',
        path: '/surveys/email-tracking',
        icon: Mail,
        description: 'Track sent emails and engagement',
        businessContext: 'Monitor email dispatches, track open rates, click-through rates, and delivery status for survey invitation and reminder emails across all surveys.'
      },
      {
        id: 'surveys-email-management',
        label: 'Email Management',
        path: '/surveys/email-management',
        icon: Send,
        description: 'Manage email sequences and templates',
        businessContext: 'Create and manage email sequences for survey invitations, reminders, submission confirmations, and processing completion notifications with AI generation support across all surveys.'
      }
    ]
  },
  {
    id: 'tools',
    label: 'Tools',
    icon: Wrench,
    description: 'Utility tools and specialized features',
    businessContext: 'Access specialized tools for time-gated content access and other utility functions',
    children: [
      {
        id: 'naming',
        label: 'Brand Naming',
        path: '/naming',
        icon: Sparkles,
        description: 'Generate and evaluate brand name candidates',
        businessContext: 'Marketing teams use AI to generate brand name ideas based on keywords, evaluate candidates, check domain availability, and validate final selections for new products, services, or brands.'
      },
      {
        id: 'time-gated-pdfs',
        label: 'Time-Gated PDFs',
        path: '/pdf-access-settings',
        icon: AlarmClockCheck,
        description: 'Manage time-gated PDF access schedules',
        businessContext: 'Configure time-based access controls for secure PDF distribution with watermarking and schedule management'
      },
      {
        id: 'url-shortener',
        label: 'URL Shortener',
        path: '/url-shortener',
        icon: Link2,
        description: 'Create and manage short URLs',
        businessContext: 'Create custom short URLs for marketing campaigns with click tracking and activity logging'
      },
      {
        id: 'incoming-transcripts',
        label: 'Incoming Transcripts',
        path: '/incoming-transcripts',
        icon: FileText,
        description: 'Central inbox for meeting transcripts and MOMs',
        businessContext: 'Review and route incoming meeting transcripts from external sources to Operations or Sales teams'
      }
    ]
  },
  {
    id: 'support-portal',
    label: 'Support Portal',
    icon: Album,
    description: 'Comprehensive help center and knowledge base',
    businessContext: 'Support colleagues create, manage, and search help articles to assist users. Features hierarchical categories, full-text search, markdown rendering, and integration with support threads.',
    children: [
      {
        id: 'support-portal-main',
        label: 'Help Center',
        path: '/support-portal',
        icon: Album,
        description: 'Browse and search support articles',
        businessContext: 'Users and support staff browse articles by category, search for specific topics, and view detailed help content with code examples and internal linking.'
      }
    ]
  },
  {
    id: '360',
    label: '360 CRM',
    icon: Building2,
    description: 'Real estate CRM and property management system',
    businessContext: 'Complete CRM solution for real estate agencies managing customers, properties, projects, proposals, campaigns, and operations. Includes customer relationship management, property listings, project tracking, proposal generation, marketing campaigns, visit scheduling, and comprehensive reporting.',
    featureKey: 'real_estate_360_crm',
    children: [
      {
        id: '360-dashboard',
        label: 'Dashboard',
        path: '/360/dashboard',
        icon: LayoutDashboard,
        description: 'Overview and analytics for 360 module',
        businessContext: 'Comprehensive dashboard showing salespeople activity, owner interactions, commercial proposals, event forecasts, client segmentation, campaign statuses, and conversion metrics.'
      },
      {
        id: '360-my-activity',
        label: 'My Activity',
        path: '/360/my-activity',
        icon: Activity,
        description: 'View your activity, proposals, events, and notifications',
        businessContext: 'Personal activity dashboard showing individual performance metrics, commercial proposals sent and received feedback, active events, and notifications from owners, clients, and campaigns. Track personal engagement and response rates.'
      },
      {
        id: '360-customers',
        label: 'Customers',
        path: '/360/customers',
        icon: Users,
        description: 'Manage customer relationships and profiles',
        businessContext: 'Central hub for all customer information including contact details, preferences, purchase history, relationships, tags, and scoring. Track customer interactions, operations, and engagement across all touchpoints.'
      },
      {
        id: '360-units',
        label: 'Units',
        path: '/360/units',
        icon: Home,
        description: 'Manage property units and listings',
        businessContext: 'Comprehensive property management including unit details, status tracking, features, environments, questions, and status history. Track availability, pricing, and property characteristics for effective sales and marketing.'
      },
      {
        id: '360-projects',
        label: 'Projects',
        path: '/360/projects',
        icon: Building2,
        description: 'Manage real estate projects',
        businessContext: 'Organize properties into projects with blocks, levels, and modules. Track project progress, unit assignments, and project-level analytics for portfolio management.'
      },
      {
        id: '360-proposals',
        label: 'Proposals',
        path: '/360/proposals',
        icon: FileCheck,
        description: 'Create and manage property proposals',
        businessContext: 'Generate professional property proposals for customers. Build customized proposals with unit details, pricing, terms, and conditions. Share proposals with customers and track acceptance status.'
      },
      {
        id: '360-events',
        label: 'Events',
        path: '/360/events',
        icon: CalendarCheck,
        description: 'Manage lead events and activities',
        businessContext: 'Track all customer interactions and events including inquiries, meetings, follow-ups, and touchpoints. Monitor event origins and customer engagement patterns.'
      },
      {
        id: '360-external-chatbot',
        label: 'Consultas externas',
        path: '/360/external-chatbot',
        icon: Bot,
        description: 'Manage external chatbot inquiries',
        businessContext: 'Review and process leads from external chatbot integrations. Approve inquiries to create events or reject invalid entries. Track inquiry status and conversion rates.'
      },
      {
        id: '360-campaigns',
        label: 'Campaigns',
        path: '/360/campaigns',
        icon: Megaphone,
        description: 'Manage marketing campaigns',
        businessContext: 'Plan and execute marketing campaigns targeting specific customer segments. Track campaign performance, customer engagement, and ROI. Link campaigns to events and customer interactions.'
      },
      {
        id: '360-visits',
        label: 'Visits',
        path: '/360/visits',
        icon: Calendar,
        description: 'Schedule and manage property visits',
        businessContext: 'Schedule property visits for customers, track visit history, collect feedback and ratings. Manage visit logistics and follow-up activities.'
      },
      {
        id: '360-log',
        label: 'Log',
        path: '/360/log',
        icon: FileText,
        description: 'View system activity and event logs',
        businessContext: 'Track all system activities, events, and important notifications. Monitor user actions, system changes, and critical events for audit and debugging purposes.'
      },
      {
        id: '360-buyer-funnel',
        label: 'Buyer Funnel',
        path: '/360/funnel/buyers',
        icon: TrendingUp,
        description: 'Manage buyer funnel stages',
        businessContext: 'Track buyers through the sales funnel from initial contact to negotiation. Use kanban board to move buyers between stages and monitor sales forecast.'
      },
      {
        id: '360-owner-funnel',
        label: 'Owner Funnel',
        path: '/360/funnel/owners',
        icon: TrendingUp,
        description: 'Manage owner funnel stages',
        businessContext: 'Track property owners through engagement stages from uncontacted to physical visits. Monitor proposals, visits, and reports sent to owners.'
      },
      {
        id: '360-loyalty-funnel',
        label: 'Loyalty Funnel',
        path: '/360/funnel/loyalty',
        icon: TrendingUp,
        description: 'Manage loyalty funnel stages',
        businessContext: 'Track customer loyalty and engagement through various stages. Monitor investor ratings, proposal listening, and purchase capacity to identify high-value customers.'
      },
      {
        id: '360-operations',
        label: 'Operations',
        path: '/360/operations',
        icon: BriefcaseIcon,
        description: 'Track business operations',
        businessContext: 'Monitor all business operations including sales, purchases, transactions, and operational activities. Track operation types, statuses, and associated customers and units.'
      },
      {
        id: '360-settings',
        label: 'Settings',
        path: '/360/settings',
        icon: Settings,
        description: 'Configure 360 module settings',
        businessContext: 'Manage all configuration settings including statuses, types, currencies, offices, reasons, and other reference data used throughout the CRM system.'
      }
    ]
  },
  {
    id: 'podcast-hosting',
    label: 'Podcast Hosting',
    icon: Mic,
    description: 'Manage podcast shows, episodes, and guest outreach',
    businessContext: 'Podcast hosts manage their shows, episodes, guests, and outreach campaigns for podcast guest recruitment and engagement.',
    children: [
      { 
        id: 'podcasthost-dashboard', 
        label: 'Dashboard', 
        path: '/podcasthost', 
        icon: LayoutDashboard,
        description: 'Overview of your podcast hosting operations',
        businessContext: 'Central dashboard for podcast hosts showing show statistics, recent episodes, guest pipeline, and campaign performance.'
      },
      { 
        id: 'podcasthost-shows', 
        label: 'Shows', 
        path: '/podcasthost/shows', 
        icon: Mic,
        description: 'Manage your podcast shows and series',
        businessContext: 'Create and manage podcast shows with descriptions, artwork, and settings. Track episode counts and guest appearances per show.'
      },
      { 
        id: 'podcasthost-hosts', 
        label: 'Hosts', 
        path: '/podcasthost/hosts', 
        icon: Users,
        description: 'Manage hosts and co-hosts for your shows',
        businessContext: 'Manage podcast hosts with profiles, bios, and contact information. Import hosts from LMS instructors.'
      },
      { 
        id: 'podcasthost-episodes', 
        label: 'All Episodes', 
        path: '/podcasthost/episodes', 
        icon: Play,
        description: 'View all episodes across all shows',
        businessContext: 'Central view of all podcast episodes across shows. Track recording dates, guest appearances, and publication status.'
      },
      { 
        id: 'podcasthost-guests', 
        label: 'Guests', 
        path: '/podcasthost/guests', 
        icon: Users,
        description: 'Manage your podcast guest database',
        businessContext: 'CRM for podcast guests including contact information, expertise areas, episode history, and outreach status.'
      },
      { 
        id: 'podcasthost-campaigns', 
        label: 'Campaigns', 
        path: '/podcasthost/campaigns', 
        icon: Mail,
        description: 'Email campaigns for podcast guests',
        businessContext: 'Create and manage email outreach campaigns to recruit potential podcast guests. Track response rates and booking conversions.'
      },
      { 
        id: 'podcasthost-email-analytics', 
        label: 'Email Analytics', 
        path: '/podcasthost/email-analytics', 
        icon: BarChart,
        description: 'Email queue and delivery analytics',
        businessContext: 'Track email delivery, opens, clicks, and bounces for podcast guest campaigns via the universal email queue.'
      },
      { 
        id: 'podcasthost-email-settings', 
        label: 'Email Settings', 
        path: '/podcasthost/email-settings', 
        icon: Mail,
        description: 'Configure email settings for podcast campaigns',
        businessContext: 'Set sender details, logos, and HTML templates for podcast guest outreach emails.'
      }
    ]
  },
  {
    id: 'events',
    label: 'Events',
    icon: Calendar,
    description: 'Manage events, speakers, leads, and campaigns',
    businessContext: 'Events module for physical and virtual events: event listings, speakers, leads capture, campaigns, certificates, and associations.',
    children: [
      { id: 'events-dashboard', label: 'Dashboard', path: '/events', icon: LayoutDashboard, description: 'Events overview', businessContext: 'Overview of events, upcoming events, leads, and campaigns.' },
      { id: 'events-list', label: 'Events', path: '/events/events', icon: Calendar, description: 'All events', businessContext: 'List and manage events with status, time, address, speakers.' },
      { id: 'events-speakers', label: 'Speakers', path: '/events/speakers', icon: Users, description: 'Manage speakers', businessContext: 'Manage event speakers and co-speakers.' },
      { id: 'events-leads', label: 'Leads', path: '/events/leads', icon: UserPlus, description: 'Event leads', businessContext: 'Manage leads for waiting list and pre-registration.' },
      { id: 'events-campaigns', label: 'Campaigns', path: '/events/campaigns', icon: Mail, description: 'Email campaigns', businessContext: 'Event email campaigns.' },
      { id: 'events-certificates', label: 'Certificates', path: '/events/certificates', icon: Award, description: 'Event certificates', businessContext: 'View and manage event completion certificates.' },
      { id: 'events-questions', label: 'Q&A', path: '/events/questions', icon: MessageCircle, description: 'Submitted Q&A', businessContext: 'View questions submitted by attendees on the public Q&A page.' },
      { id: 'events-certificate-templates', label: 'Certificate Templates', path: '/events/certificate-templates', icon: FileText, description: 'Design and manage certificate templates for events', businessContext: 'Create customizable certificate templates for event attendance certificates.' },
      { id: 'events-associations', label: 'Associations', path: '/events/associations', icon: Building2, description: 'Event associations', businessContext: 'Manage associations for events.' },
      { id: 'events-association-types', label: 'Association Types', path: '/events/association-types', icon: Tags, description: 'Event association types (e.g. Sponsor, Partner)', businessContext: 'Manage types used when linking associations to events.' },
      {
        id: 'events-public-path',
        label: 'Public Path',
        icon: Store,
        description: 'Customer-facing event pages',
        businessContext: 'Public pages for event participants to access materials and certificates.',
        children: [
          { id: 'events-thanks-page', label: 'Thanks for Participating', path: '/event-materials/:brandSlug/:eventSlug', icon: Award, description: 'Public thank you page where participants can request event materials and certificates', businessContext: 'Post-event page for attendees to download resources, request materials, and receive participation certificates.' },
          { id: 'events-check-in-page', label: 'Check In', path: '/event-check-in/:brandSlug/:eventSlug', icon: ClipboardCheck, description: 'Public check-in page for attendees to record attendance', businessContext: 'Event attendees submit name and email to record attendance (updates lead to status: attended, type: engagement).' },
          { id: 'events-questions-page', label: 'Q&A', path: '/event-questions/:brandSlug/:eventSlug', icon: MessageCircle, description: 'Public Q&A page for attendees to ask and view questions', businessContext: 'Live Q&A where attendees submit questions and see others\' questions.' }
        ]
      },
      { id: 'events-email-analytics', label: 'Email Analytics', path: '/events/email-analytics', icon: BarChart, description: 'Email queue and delivery analytics', businessContext: 'Track email delivery, opens, clicks, and bounces for event campaigns via the universal email queue.' }
    ]
  }
];
